import { useEffect, useState } from "react";
import { NavLink, Route, Routes, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Calendar, Users, CreditCard, Send, BarChart3, LogOut, Plus, Check, X, Euro, QrCode, ShieldCheck, Trash2, ArrowLeft, ExternalLink, History, Download, Filter, Home, Pencil, Search, Upload, Mail, Eye, Paperclip, Bell, Clock, UserPlus, Repeat2, Map as MapIcon, LayoutGrid, MapPin, Ban, Menu, ShoppingBag } from "lucide-react";
import QRScanner from "../components/QRScanner";
import ProspectsMap from "../components/ProspectsMap";
import SessionsCalendar from "../components/SessionsCalendar";
import AdminShop from "./AdminShop";
import { computeRecurrenceDates } from "../lib/recurrence";

const ACTIVITY_OPTIONS = [
  { value: "", label: "— non renseigné —" },
  { value: "nordic_yoga", label: "Nordic Yoga" },
  { value: "nordic_walking", label: "Nordic Walking" },
  { value: "both", label: "Les deux" },
  { value: "other", label: "Autre" },
];
const ACTIVITY_LABEL = Object.fromEntries(ACTIVITY_OPTIONS.map(o => [o.value, o.label]));

const PROSPECT_STATUS_OPTIONS = [
  { value: "nouveau", label: "Nouveau", cls: "badge-stage" },
  { value: "contacte", label: "Contacté", cls: "badge-marche" },
  { value: "interesse", label: "Intéressé", cls: "badge-yoga" },
  { value: "converti", label: "Converti", cls: "badge-yoga" },
  { value: "perdu", label: "Perdu", cls: "badge-marche" },
];
const PROSPECT_STATUS_MAP = Object.fromEntries(PROSPECT_STATUS_OPTIONS.map(s => [s.value, s]));

function isOverdue(p) {
  if (!p.next_contact_at) return false;
  if (p.status === "converti" || p.status === "perdu") return false;
  const today = new Date().toISOString().slice(0, 10);
  return p.next_contact_at.slice(0, 10) < today;
}

function fmt(d) { return new Date(d).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" }); }

function Overview() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [nextDiscovery, setNextDiscovery] = useState(null);
  const [discoveryStats, setDiscoveryStats] = useState([]);
  useEffect(() => {
    if (user?.role !== "admin") return;
    api.get("/admin/stats").then(r => setStats(r.data)).catch(() => {});
    api.get("/admin/discovery/next").then(r => setNextDiscovery(r.data || null)).catch(() => {});
    api.get("/admin/discovery/stats").then(r => setDiscoveryStats(r.data || [])).catch(() => {});
  }, [user]);
  if (user?.role !== "admin") {
    return (
      <div>
        <h1 className="font-serif-display text-4xl font-semibold" style={{ color: "var(--pine)" }}>Bonjour {user?.name}</h1>
        <p className="mt-2 text-[var(--muted-text)]">Bienvenue dans votre espace coach. Sélectionnez <strong>Séances</strong> à gauche pour pointer les présences et scanner les badges.</p>
      </div>
    );
  }
  if (!stats) return <div>Chargement…</div>;
  const items = [
    { label: "Séances à venir", val: stats.upcoming_sessions, testid: "stat-upcoming" },
    { label: "Participants", val: stats.total_participants, testid: "stat-participants" },
    { label: "Réservations ce mois", val: stats.bookings_this_month, testid: "stat-bookings" },
    { label: "Revenus ce mois", val: stats.revenue_this_month_eur + " €", testid: "stat-revenue" },
    { label: "Taux de présence", val: stats.attendance_rate_pct + " %", testid: "stat-attendance" },
  ];
  const discLabel = (d) => d === "nordic_yoga" ? "Nordic Yoga®" : d === "marche_nordique" ? "Marche Nordique" : "Stage";
  const fmtWhen = (iso) => new Date(iso).toLocaleString("fr-FR", { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" });
  return (
    <div>
      <h1 className="font-serif-display text-4xl font-semibold" style={{ color: "var(--pine)" }}>Tableau de bord</h1>
      <p className="mt-2 text-[var(--muted-text)]">Vue synthétique de votre activité.</p>

      {nextDiscovery && (
        <div className="mt-8 rounded-2xl overflow-hidden" style={{ background: "linear-gradient(135deg, var(--terracotta) 0%, #B85F35 100%)" }} data-testid="dashboard-next-discovery">
          <div className="p-6 md:p-8 text-white">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest opacity-85">
                  🌱 Prochaine séance découverte · {discLabel(nextDiscovery.discipline)}
                </div>
                <h2 className="font-serif-display text-2xl md:text-3xl font-semibold mt-2" data-testid="dashboard-next-discovery-title">{nextDiscovery.title}</h2>
                <div className="mt-2 text-sm opacity-90">📅 {fmtWhen(nextDiscovery.start_at)}</div>
                <div className="text-sm opacity-90">📍 {nextDiscovery.location}</div>
              </div>
              <div className="grid grid-cols-3 gap-4 md:min-w-[260px]">
                {[
                  { label: "Invités", val: nextDiscovery.invited, tid: "dashboard-next-discovery-invited" },
                  { label: "Inscrits", val: nextDiscovery.booked, tid: "dashboard-next-discovery-booked" },
                  { label: "Places", val: nextDiscovery.places_left, tid: "dashboard-next-discovery-left" },
                ].map(k => (
                  <div key={k.label} className="bg-white/15 backdrop-blur rounded-xl p-3 text-center" data-testid={k.tid}>
                    <div className="text-3xl font-serif-display font-semibold">{k.val}</div>
                    <div className="text-[11px] uppercase tracking-wide opacity-85 mt-0.5">{k.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map(it => (
          <div key={it.label} className="card-warm p-6" data-testid={it.testid}>
            <div className="overline">{it.label}</div>
            <div className="mt-3 font-serif-display text-4xl font-semibold" style={{ color: "var(--pine)" }}>{it.val}</div>
          </div>
        ))}
      </div>

      {discoveryStats.length > 0 && (
        <div className="mt-10" data-testid="dashboard-discovery-stats">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <h2 className="font-serif-display text-2xl font-semibold" style={{ color: "var(--pine)" }}>🌱 Performance des séances découverte</h2>
            <span className="text-xs text-[var(--muted-text)]">{discoveryStats.length} séance{discoveryStats.length > 1 ? "s" : ""} à venir</span>
          </div>
          <div className="card-warm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[var(--sand)] text-left text-[var(--muted-text)]">
                <tr>
                  <th className="p-4">Séance</th>
                  <th className="p-4">Date</th>
                  <th className="p-4 text-center">Invités</th>
                  <th className="p-4 text-center">Ouvertures</th>
                  <th className="p-4 text-center">Inscrits</th>
                  <th className="p-4 text-center">Places</th>
                  <th className="p-4 text-center">Conversion</th>
                </tr>
              </thead>
              <tbody>
                {discoveryStats.map(row => (
                  <tr key={row.id} className="border-t" data-testid={`dashboard-discovery-row-${row.id}`}>
                    <td className="p-4">
                      <div className="font-medium text-[var(--pine)]">{row.title}</div>
                      <div className="text-xs text-[var(--muted-text)] mt-0.5">{discLabel(row.discipline)} · {row.location}</div>
                    </td>
                    <td className="p-4 text-[var(--muted-text)]">{fmtWhen(row.start_at)}</td>
                    <td className="p-4 text-center font-semibold" data-testid={`dashboard-discovery-invited-${row.id}`}>{row.invited}</td>
                    <td className="p-4 text-center">
                      <span className="inline-flex flex-col items-center">
                        <span className="font-semibold">{row.opened}</span>
                        <span className="text-[10px] text-[var(--muted-text)]">{row.open_rate_pct}%</span>
                      </span>
                    </td>
                    <td className="p-4 text-center font-semibold" data-testid={`dashboard-discovery-booked-${row.id}`}>{row.booked}</td>
                    <td className="p-4 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${row.places_left === 0 ? "bg-red-100 text-red-700" : row.places_left <= 2 ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                        {row.places_left} / {row.max_participants}
                      </span>
                    </td>
                    <td className="p-4 text-center">
                      <span className="inline-flex items-center gap-1 text-[var(--forest)] font-semibold">{row.conversion_pct}%</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Sessions() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingSeries, setEditingSeries] = useState(null);  // series_id of the session being edited
  const [disciplineFilter, setDisciplineFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("upcoming");
  const [viewMode, setViewMode] = useState("list"); // "list" | "calendar"
  const emptyForm = { title: "", discipline: "nordic_yoga", start_at: "", duration_min: 60, location: "", lat: "", lng: "", max_participants: 12, price_eur: "", coach: "", description: "", is_discovery: false };
  const [form, setForm] = useState(emptyForm);
  const [recurrence, setRecurrence] = useState({ enabled: false, pattern: "weekly", interval: 1, count: 8 });
  const [applyToSeries, setApplyToSeries] = useState(false);

  const load = () => api.get("/sessions").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  // Suggest last price for the picked discipline when creating a new session (empty price).
  useEffect(() => {
    if (!creating || editingId) return;
    if (form.price_eur !== "" && form.price_eur !== 0) return;
    const same = items
      .filter(s => s.discipline === form.discipline && !s.cancelled && Number(s.price_eur) > 0)
      .sort((a, b) => new Date(b.start_at) - new Date(a.start_at));
    if (same.length > 0) {
      setForm(prev => ({ ...prev, price_eur: same[0].price_eur }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creating, form.discipline, items]);

  const openEdit = (s) => {
    // datetime-local expects "YYYY-MM-DDTHH:mm" in local time.
    // s.start_at comes as ISO UTC ('...Z' or '+00:00'); Date() converts to local, so getHours() gives the correct wall-clock time for the user.
    const d = new Date(s.start_at);
    const pad = (n) => String(n).padStart(2, "0");
    const local = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    setForm({
      title: s.title, discipline: s.discipline, start_at: local,
      duration_min: s.duration_min, location: s.location,
      lat: s.lat != null ? String(s.lat) : "", lng: s.lng != null ? String(s.lng) : "",
      max_participants: s.max_participants, price_eur: s.price_eur,
      coach: s.coach || "", description: s.description || "",
      is_discovery: !!s.is_discovery,
    });
    setEditingId(s.id);
    setEditingSeries(s.series_id || null);
    setApplyToSeries(false);
    setCreating(true);
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 50);
  };

  const cancelEdit = () => { setEditingId(null); setEditingSeries(null); setApplyToSeries(false); setCreating(false); setForm(emptyForm); };

  const save = async (e) => {
    e.preventDefault();
    try {
      const body = {
        ...form,
        start_at: new Date(form.start_at).toISOString(),
        duration_min: Number(form.duration_min),
        max_participants: Number(form.max_participants),
        price_eur: Number(form.price_eur),
        lat: form.lat === "" ? null : Number(form.lat),
        lng: form.lng === "" ? null : Number(form.lng),
        is_discovery: !!form.is_discovery,
      };
      if (editingId) {
        const url = applyToSeries && editingSeries ? `/sessions/${editingId}?apply_to_series=future` : `/sessions/${editingId}`;
        const { data } = await api.put(url, body);
        if (data.series_updated) toast.success(`${data.series_updated} séance(s) de la série mises à jour`);
        else toast.success("Séance mise à jour");
      } else {
        let url = "/sessions";
        if (recurrence.enabled) {
          const params = new URLSearchParams({
            repeat_pattern: recurrence.pattern,
            repeat_interval: String(recurrence.interval),
            repeat_count: String(recurrence.count),
          });
          url = `/sessions?${params.toString()}`;
        }
        const { data } = await api.post(url, body);
        toast.success(data.created_count > 1 ? `${data.created_count} séances créées (série)` : "Séance créée");
        setRecurrence({ enabled: false, pattern: "weekly", interval: 1, count: 8 });
      }
      cancelEdit();
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    const target = items.find(i => i.id === id);
    let applySeries = false;
    if (target?.series_id) {
      const choice = window.prompt(
        "Cette séance fait partie d'une série récurrente.\n\nTapez OUI pour supprimer aussi toutes les occurrences futures.\nTapez NON (ou laissez vide) pour ne supprimer que cette séance.",
        "NON"
      );
      if (choice === null) return;
      applySeries = choice.trim().toUpperCase() === "OUI";
    } else {
      if (!window.confirm("Supprimer cette séance ?")) return;
    }
    const url = applySeries ? `/sessions/${id}?apply_to_series=future` : `/sessions/${id}`;
    const { data } = await api.delete(url);
    toast.success(data.deleted_count > 1 ? `${data.deleted_count} séances supprimées` : "Séance supprimée");
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-serif-display text-4xl font-semibold" style={{ color: "var(--pine)" }}>Agenda des séances</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex items-center rounded-full bg-slate-100 p-1 text-xs" data-testid="sessions-view-toggle">
            {[
              { v: "list", label: "Liste" },
              { v: "calendar", label: "Calendrier" },
            ].map(o => (
              <button key={o.v} onClick={() => setViewMode(o.v)}
                className={`px-3 py-1.5 rounded-full transition inline-flex items-center gap-1 ${viewMode === o.v ? "bg-[var(--pine)] text-white" : "text-[var(--muted-text)] hover:text-[var(--pine)]"}`}
                data-testid={`sessions-view-${o.v}`}>
                {o.v === "calendar" ? <LayoutGrid size={12}/> : <Calendar size={12}/>} {o.label}
              </button>
            ))}
          </div>
          {canEdit && (
            <button onClick={() => { if (creating) cancelEdit(); else setCreating(true); }} className="btn-primary" data-testid="admin-new-session">
              <Plus size={16}/> Nouvelle séance
            </button>
          )}
        </div>
      </div>

      {creating && canEdit && (
        <form onSubmit={save} className="card-warm p-6 mt-6 grid sm:grid-cols-2 gap-4" data-testid="session-form">
          <div className="sm:col-span-2 flex items-center justify-between">
            <h2 className="font-serif-display text-2xl" style={{ color: "var(--pine)" }}>{editingId ? "Modifier la séance" : "Nouvelle séance"}</h2>
            {editingId && <button type="button" onClick={cancelEdit} className="text-sm text-[var(--muted-text)] hover:underline" data-testid="session-form-cancel">Annuler</button>}
          </div>
          <input required placeholder="Titre" className="input-field" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="session-form-title" />
          <select className="input-field" value={form.discipline} onChange={e => setForm({ ...form, discipline: e.target.value })} data-testid="session-form-discipline">
            <option value="nordic_yoga">Nordic Yoga</option>
            <option value="marche_nordique">Marche Nordique</option>
            <option value="stage">Stage</option>
          </select>
          <input required type="datetime-local" className="input-field" value={form.start_at} onChange={e => setForm({ ...form, start_at: e.target.value })} data-testid="session-form-date" />
          <input required type="number" min="15" placeholder="Durée (min)" className="input-field" value={form.duration_min} onChange={e => setForm({ ...form, duration_min: e.target.value })} />
          <input required placeholder="Lieu" className="input-field sm:col-span-2" value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} data-testid="session-form-location" />
          <div className="sm:col-span-2 flex gap-2 items-center">
            <input type="number" step="any" placeholder="Latitude (ex : 50.8503)" className="input-field flex-1" value={form.lat} onChange={e => setForm({ ...form, lat: e.target.value })} data-testid="session-form-lat" />
            <input type="number" step="any" placeholder="Longitude (ex : 4.3517)" className="input-field flex-1" value={form.lng} onChange={e => setForm({ ...form, lng: e.target.value })} data-testid="session-form-lng" />
            <button
              type="button"
              onClick={async () => {
                if (!form.location.trim()) { toast.error("Renseignez d'abord le lieu"); return; }
                try {
                  const { data } = await api.post("/admin/geocode", { address: form.location });
                  setForm(f => ({ ...f, lat: String(data.lat), lng: String(data.lng) }));
                  toast.success("Coordonnées trouvées");
                } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Adresse introuvable"); }
              }}
              className="btn-ghost border inline-flex items-center gap-1 whitespace-nowrap"
              data-testid="session-form-geocode"
            ><MapPin size={14}/> Géolocaliser</button>
          </div>
          <input required type="number" min="1" placeholder="Places max" className="input-field" value={form.max_participants} onChange={e => setForm({ ...form, max_participants: e.target.value })} />
          <input required type="number" step="0.01" min="0" placeholder="Prix (€) *" className="input-field" value={form.price_eur} onChange={e => setForm({ ...form, price_eur: e.target.value })} />
          <input placeholder="Coach" className="input-field sm:col-span-2" value={form.coach} onChange={e => setForm({ ...form, coach: e.target.value })} />
          <textarea placeholder="Description" className="input-field sm:col-span-2" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
          <label className="sm:col-span-2 flex items-start gap-2 p-3 rounded-lg border border-[var(--terracotta)]/30 bg-[var(--terracotta)]/5 cursor-pointer" data-testid="session-form-discovery-wrap">
            <input type="checkbox" checked={!!form.is_discovery} onChange={e => setForm({ ...form, is_discovery: e.target.checked, price_eur: e.target.checked && form.price_eur === "" ? 0 : form.price_eur })} className="mt-1" data-testid="session-form-discovery" />
            <div>
              <div className="text-sm font-semibold text-[var(--pine)]">🌱 Séance découverte</div>
              <div className="text-xs text-[var(--muted-text)] mt-0.5">Séance mise en avant pour capter les prospects. Généralement gratuite. Après création, vous pourrez inviter tous vos prospects en un clic.</div>
            </div>
          </label>
          {!editingId && (
            <div className="sm:col-span-2 p-3 rounded-lg bg-[var(--forest-light)]/40 border border-[var(--forest)]/20 space-y-3" data-testid="session-form-recurrence">
              <label className="text-sm text-[var(--pine)] font-medium flex items-center gap-2">
                <input type="checkbox" checked={recurrence.enabled} onChange={e => setRecurrence(r => ({ ...r, enabled: e.target.checked }))} data-testid="session-form-recurrence-toggle" />
                Créer une série récurrente
              </label>
              {recurrence.enabled && (
                <>
                  <div className="grid sm:grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs text-[var(--muted-text)] block mb-1">Cadence</label>
                      <select className="input-field" value={recurrence.pattern} onChange={e => setRecurrence(r => ({ ...r, pattern: e.target.value, interval: 1 }))} data-testid="session-form-recurrence-pattern">
                        <option value="weekly">Hebdomadaire</option>
                        <option value="monthly_weekday">Mensuel (même jour de la semaine)</option>
                      </select>
                    </div>
                    {recurrence.pattern === "weekly" && (
                      <div>
                        <label className="text-xs text-[var(--muted-text)] block mb-1">Toutes les</label>
                        <select className="input-field" value={recurrence.interval} onChange={e => setRecurrence(r => ({ ...r, interval: Number(e.target.value) }))} data-testid="session-form-recurrence-interval">
                          <option value={1}>1 semaine</option>
                          <option value={2}>2 semaines</option>
                          <option value={3}>3 semaines</option>
                          <option value={4}>4 semaines</option>
                        </select>
                      </div>
                    )}
                    <div>
                      <label className="text-xs text-[var(--muted-text)] block mb-1">Nombre de séances</label>
                      <input type="number" min="1" max="52" className="input-field" value={recurrence.count} onChange={e => setRecurrence(r => ({ ...r, count: Math.max(1, Math.min(52, Number(e.target.value) || 1)) }))} data-testid="session-form-recurrence-count" />
                    </div>
                  </div>
                  {form.start_at && (() => {
                    const base = new Date(form.start_at);
                    if (isNaN(base.getTime())) return null;
                    const dates = computeRecurrenceDates(base, recurrence.pattern, recurrence.interval, recurrence.count);
                    return (
                      <div className="mt-2" data-testid="session-form-recurrence-preview">
                        <div className="overline mb-2">Aperçu des dates</div>
                        <div className="flex flex-wrap gap-2 max-h-40 overflow-auto">
                          {dates.map((d, i) => (
                            <div key={i} className="text-xs px-3 py-1.5 rounded-full bg-white border border-[var(--forest)]/30 text-[var(--pine)]" data-testid={`recurrence-preview-${i}`}>
                              {d.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short", year: "numeric" })} · {d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </>
              )}
            </div>
          )}
          {editingId && editingSeries && (
            <div className="sm:col-span-2 p-3 rounded-lg bg-amber-50 border border-amber-200 flex items-center gap-2 text-sm" data-testid="session-form-apply-series">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={applyToSeries} onChange={e => setApplyToSeries(e.target.checked)} data-testid="session-form-apply-series-toggle" />
                <span>Appliquer les modifications à <strong>toutes les occurrences futures</strong> de cette série</span>
              </label>
            </div>
          )}
          <button type="submit" className="btn-primary sm:col-span-2 justify-center" data-testid="session-form-submit">
            {editingId ? "Enregistrer les modifications" : (recurrence.enabled ? `Créer ${recurrence.count} séance(s)` : "Créer la séance")}
          </button>
        </form>
      )}

      <div className="mt-6 flex items-center gap-2 flex-wrap" data-testid="sessions-filters">
        {[
          { value: "all", label: "Toutes", count: items.length },
          { value: "nordic_yoga", label: "Nordic Yoga®", count: items.filter(i => i.discipline === "nordic_yoga").length },
          { value: "marche_nordique", label: "Marche Nordique", count: items.filter(i => i.discipline === "marche_nordique").length },
          { value: "stage", label: "Stages", count: items.filter(i => i.discipline === "stage").length },
        ].map(f => (
          <button key={f.value} onClick={() => setDisciplineFilter(f.value)}
            className={`px-4 py-1.5 rounded-full text-sm inline-flex items-center gap-1.5 transition ${disciplineFilter === f.value ? "bg-[var(--pine)] text-white" : "bg-white text-[var(--muted-text)] border border-slate-200 hover:border-[var(--forest)]"}`}
            data-testid={`sessions-filter-${f.value}`}>
            {f.label}
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${disciplineFilter === f.value ? "bg-white/20" : "bg-slate-100"}`}>{f.count}</span>
          </button>
        ))}
      </div>
      <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="sessions-status-filters">
        {(() => {
          const now = Date.now();
          const buckets = {
            upcoming: items.filter(s => !s.cancelled && new Date(s.start_at).getTime() >= now),
            past: items.filter(s => !s.cancelled && new Date(s.start_at).getTime() < now),
            cancelled: items.filter(s => !!s.cancelled),
            all: items,
          };
          return [
            { value: "upcoming", label: "À venir", cls: "text-[var(--forest)]", count: buckets.upcoming.length },
            { value: "past", label: "Passées", cls: "text-[var(--muted-text)]", count: buckets.past.length },
            { value: "cancelled", label: "Annulées", cls: "text-red-600", count: buckets.cancelled.length },
            { value: "all", label: "Tous statuts", cls: "text-[var(--pine)]", count: buckets.all.length },
          ].map(f => (
            <button key={f.value} onClick={() => setStatusFilter(f.value)}
              className={`px-3 py-1 rounded-full text-xs inline-flex items-center gap-1.5 transition ${statusFilter === f.value ? "bg-[var(--terracotta)] text-white" : `bg-white ${f.cls} border border-slate-200 hover:border-[var(--terracotta)]`}`}
              data-testid={`sessions-status-${f.value}`}>
              {f.label}
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${statusFilter === f.value ? "bg-white/20" : "bg-slate-100 text-slate-600"}`}>{f.count}</span>
            </button>
          ));
        })()}
      </div>

      {(() => {
        const now = Date.now();
        let statusItems = items;
        if (statusFilter === "upcoming") statusItems = items.filter(s => !s.cancelled && new Date(s.start_at).getTime() >= now);
        else if (statusFilter === "past") statusItems = items.filter(s => !s.cancelled && new Date(s.start_at).getTime() < now);
        else if (statusFilter === "cancelled") statusItems = items.filter(s => !!s.cancelled);
        const disciplineFiltered = disciplineFilter === "all" ? statusItems : statusItems.filter(s => s.discipline === disciplineFilter);
        if (viewMode === "calendar") {
          return (
            <div className="mt-4">
              <SessionsCalendar
                sessions={disciplineFiltered}
                onSelect={(s) => { if (canEdit) openEdit(s); }}
                testidPrefix="admin-sessions-calendar"
              />
            </div>
          );
        }
        const groups = [
          { key: "nordic_yoga", label: "Nordic Yoga®", cls: "badge-yoga" },
          { key: "marche_nordique", label: "Marche Nordique", cls: "badge-marche" },
          { key: "stage", label: "Stages", cls: "badge-stage" },
        ];
        const visible = disciplineFilter === "all" ? groups : groups.filter(g => g.key === disciplineFilter);
        const anyVisible = visible.some(g => statusItems.some(i => i.discipline === g.key));
        return (
          <div className="mt-4 space-y-8">
            {!anyVisible && (
              <div className="card-warm p-12 text-center text-[var(--muted-text)]">
                Aucune séance dans cette catégorie.
              </div>
            )}
            {visible.map(g => {
              const list = statusItems.filter(s => s.discipline === g.key);
              if (list.length === 0) return null;
              return (
                <section key={g.key} data-testid={`sessions-group-${g.key}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <span className={`badge-discipline ${g.cls}`}>{g.label}</span>
                    <span className="text-xs text-[var(--muted-text)]">{list.length} séance{list.length > 1 ? "s" : ""}</span>
                    <div className="flex-1 h-px bg-slate-200"></div>
                  </div>
                  <div className="space-y-3">
                    {list.map(s => (
                      <SessionRow key={s.id} s={s} canEdit={canEdit} onEdit={() => openEdit(s)} onDelete={() => remove(s.id)} onRefresh={load} />
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        );
      })()}
    </div>
  );
}

function SessionRow({ s, canEdit, onEdit, onDelete, onRefresh }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [open, setOpen] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [scanOpen, setScanOpen] = useState(false);
  const loadParts = () => api.get(`/sessions/${s.id}/participants`).then(r => setParticipants(r.data));
  useEffect(() => { if (open) loadParts(); }, [open]);

  const toggleAttendance = async (bid, present) => {
    await api.post(`/bookings/${bid}/attendance?present=${present}`);
    toast.success(present ? "Présent" : "Absent");
    loadParts();
  };
  const markPaid = async (bid) => {
    await api.post(`/bookings/${bid}/mark-paid`, { method: "cash", note: "" });
    toast.success("Marqué payé");
    loadParts();
    onRefresh();
  };

  return (
    <div className="card-warm p-5">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <span className={`badge-discipline ${s.discipline === "nordic_yoga" ? "badge-yoga" : s.discipline === "marche_nordique" ? "badge-marche" : "badge-stage"}`}>
              {s.discipline === "nordic_yoga" ? "Nordic Yoga" : s.discipline === "marche_nordique" ? "Marche" : "Stage"}
            </span>
            <h3 className={`font-semibold text-lg ${s.cancelled ? "line-through opacity-60" : ""}`} style={{ color: "var(--pine)" }}>{s.title}</h3>
            {Number(s.price_eur) === 0 && !s.cancelled && (
              <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[var(--terracotta)] text-white uppercase tracking-wide" data-testid={`admin-session-free-${s.id}`}>Offert</span>
            )}
            {s.is_discovery && !s.cancelled && (
              <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[var(--forest)] text-white uppercase tracking-wide inline-flex items-center gap-1" data-testid={`admin-session-discovery-${s.id}`}>🌱 Découverte</span>
            )}
            {s.cancelled && <span className="badge-discipline badge-marche" data-testid={`admin-session-cancelled-${s.id}`}>Annulée{s.cancel_reason ? ` · ${s.cancel_reason}` : ""}</span>}
          </div>
          <div className="mt-2 text-sm text-[var(--muted-text)]">{fmt(s.start_at)} · {s.location} · {s.booked_count}/{s.max_participants} inscrits · {Number(s.price_eur) === 0 ? "gratuit" : `${s.price_eur}€`}</div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setOpen(!open)} className="btn-ghost text-sm" data-testid={`admin-session-toggle-${s.id}`}>{open ? "Fermer" : "Inscrits & pointage"}</button>
          {canEdit && (
            s.cancelled ? (
              <button onClick={async () => {
                try { await api.post(`/sessions/${s.id}/uncancel`); toast.success("Séance réactivée"); onRefresh(); }
                catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
              }} className="text-[var(--forest)] text-sm px-3 py-1 hover:bg-emerald-50 rounded-full" data-testid={`admin-session-uncancel-${s.id}`}>Réactiver</button>
            ) : (
              <button onClick={async () => {
                const reason = window.prompt("Motif de l'annulation (ex : jour férié, météo…) — laissez vide si non applicable :", "");
                if (reason === null) return;
                try { await api.post(`/sessions/${s.id}/cancel`, { reason }); toast.success("Séance annulée"); onRefresh(); }
                catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
              }} className="text-amber-700 text-sm px-3 py-1 hover:bg-amber-50 rounded-full inline-flex items-center gap-1" data-testid={`admin-session-cancel-${s.id}`}><Ban size={12}/> Annuler</button>
            )
          )}
          {canEdit && s.is_discovery && !s.cancelled && (
            <button onClick={async () => {
              if (!window.confirm("Envoyer une invitation email à tous les prospects actifs (statut Nouveau/Contacté/Intéressé) qui n'ont pas encore été invités à cette séance ?")) return;
              try {
                const { data } = await api.post(`/admin/sessions/${s.id}/invite-prospects`);
                if (data.sent === 0 && data.skipped === 0 && data.total === 0) {
                  toast.info("Aucun prospect actif à inviter");
                } else {
                  let msg = `${data.sent} invitation(s) envoyée(s)`;
                  if (data.skipped > 0) msg += ` · ${data.skipped} déjà invité(s)`;
                  if (data.failed > 0) msg += ` · ${data.failed} échec(s)`;
                  data.failed > 0 ? toast.warning(msg) : toast.success(msg);
                }
              } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
            }} className="text-[var(--forest)] text-sm px-3 py-1 hover:bg-emerald-50 rounded-full inline-flex items-center gap-1" data-testid={`admin-session-invite-${s.id}`} title="Inviter tous les prospects par email">
              <UserPlus size={12}/> Inviter les prospects
            </button>
          )}
          {canEdit && <button onClick={onEdit} className="text-[var(--pine)] text-sm px-3 py-1 hover:bg-slate-100 rounded-full" data-testid={`admin-session-edit-${s.id}`}>Modifier</button>}
          {canEdit && <button onClick={async () => {
            try {
              const { data } = await api.post(`/sessions/${s.id}/duplicate?days=7`);
              toast.success(`Séance dupliquée au ${new Date(data.start_at).toLocaleDateString("fr-FR")}`);
              onRefresh();
            } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
          }} className="text-[var(--forest)] text-sm px-3 py-1 hover:bg-emerald-50 rounded-full" data-testid={`admin-session-duplicate-${s.id}`}>Dupliquer +7j</button>}
          {canEdit && <button onClick={onDelete} className="text-red-600 text-sm px-3 py-1 hover:bg-red-50 rounded-full" data-testid={`admin-session-delete-${s.id}`}>Supprimer</button>}
        </div>
      </div>
      {open && (
        <div className="mt-4 border-t pt-4 space-y-2">
          <div className="flex justify-end mb-2">
            <button onClick={() => setScanOpen(true)} className="btn-primary text-xs" data-testid={`admin-scan-open-${s.id}`}>
              <QrCode size={14}/> Scanner un badge
            </button>
          </div>
          {participants.length === 0 && <div className="text-sm text-[var(--muted-text)]">Personne d'inscrit.</div>}
          {participants.map(p => (
            <div key={p.booking_id} className="flex items-center justify-between p-3 rounded-lg bg-[var(--sand)]" data-testid={`admin-participant-${p.booking_id}`}>
              <div>
                <div className="font-medium">{p.name}</div>
                <div className="text-xs text-[var(--muted-text)]">{p.email} · {p.phone || "—"}</div>
              </div>
              <div className="flex gap-2 items-center">
                {p.paid ? (
                  <span className="badge-discipline badge-yoga" data-testid={`payment-status-badge-${p.booking_id}`}>Payé ({p.payment_method})</span>
                ) : isAdmin ? (
                  <button onClick={() => markPaid(p.booking_id)} className="btn-ghost text-xs border" data-testid={`admin-mark-paid-${p.booking_id}`}><Euro size={12}/> Marquer payé</button>
                ) : (
                  <span className="badge-discipline badge-marche">Non payé</span>
                )}
                <button onClick={() => toggleAttendance(p.booking_id, !p.attended)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold ${p.attended ? "bg-[var(--forest)] text-white" : "bg-slate-200 text-slate-700"}`}
                  data-testid={`admin-attendance-toggle-${p.booking_id}`}>
                  {p.attended ? <><Check size={12}/> Présent</> : <><X size={12}/> Absent</>}
                </button>
              </div>
            </div>
          ))}
          <QRScanner open={scanOpen} onClose={() => setScanOpen(false)} onResult={loadParts} />
        </div>
      )}
    </div>
  );
}

function Participants() {
  const [items, setItems] = useState([]);
  const [carnets, setCarnets] = useState([]);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null); // { mode: 'create' | 'edit' | 'import' | 'email' | 'history', participant? }
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", address: "", postal_code: "", city: "", activity: "" });
  const [emailForm, setEmailForm] = useState({ subject: "", body: "" });
  const [emailFiles, setEmailFiles] = useState([]);
  const [importForm, setImportForm] = useState({ csv: "" });
  const [importResult, setImportResult] = useState(null);
  const [history, setHistory] = useState(null);

  const load = () => api.get("/admin/participants").then(r => setItems(r.data));
  useEffect(() => { load(); api.get("/carnets").then(r => setCarnets(r.data)); }, []);

  const filtered = items.filter(p => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (p.name || "").toLowerCase().includes(q) || (p.email || "").toLowerCase().includes(q) || (p.phone || "").toLowerCase().includes(q);
  });

  const grantCarnet = async (email, carnetId) => {
    if (!carnetId) return;
    try {
      await api.post(`/admin/grant-carnet?user_email=${encodeURIComponent(email)}&carnet_id=${carnetId}`);
      toast.success("Carnet attribué");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openCreate = () => { setForm({ name: "", email: "", phone: "", password: "", address: "", postal_code: "", city: "", activity: "" }); setModal({ mode: "create" }); };
  const openEdit = (p) => { setForm({ name: p.name || "", email: p.email || "", phone: p.phone || "", password: "", address: p.address || "", postal_code: p.postal_code || "", city: p.city || "", activity: p.activity || "" }); setModal({ mode: "edit", participant: p }); };
  const openEmail = (p) => { setEmailForm({ subject: "", body: "" }); setEmailFiles([]); setModal({ mode: "email", participant: p }); };
  const openImport = () => { setImportForm({ csv: "" }); setImportResult(null); setModal({ mode: "import" }); };
  const openHistory = async (p) => {
    setModal({ mode: "history", participant: p });
    setHistory(null);
    try {
      const { data } = await api.get(`/admin/participants/${p.id}/history`);
      setHistory(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (modal.mode === "create") {
        const { data } = await api.post("/admin/participants", {
          name: form.name, email: form.email, phone: form.phone,
          password: form.password || undefined,
          address: form.address, postal_code: form.postal_code, city: form.city, activity: form.activity || "",
        });
        if (data.generated_password) {
          toast.success(`Participant créé. Mot de passe temporaire : ${data.generated_password}`, { duration: 12000 });
        } else toast.success("Participant créé");
      } else if (modal.mode === "edit") {
        await api.put(`/admin/participants/${modal.participant.id}`, {
          name: form.name, email: form.email, phone: form.phone,
          address: form.address, postal_code: form.postal_code, city: form.city, activity: form.activity || "",
        });
        toast.success("Participant mis à jour");
      }
      setModal(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sendEmail = async (e) => {
    e.preventDefault();
    try {
      const html = `<div style="font-family:Arial,sans-serif;padding:24px">${emailForm.body.replace(/\n/g, "<br/>")}</div>`;
      const fd = new FormData();
      fd.append("subject", emailForm.subject);
      fd.append("html", html);
      emailFiles.forEach(f => fd.append("files", f, f.name));
      const { data } = await api.post(`/admin/participants/${modal.participant.id}/email`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.attachments_ignored > 0) {
        toast.warning(`Email envoyé à ${modal.participant.email} — ${data.attachments_ignored} pièce(s) jointe(s) non prise(s) en charge`);
      } else {
        toast.success(`Email envoyé à ${modal.participant.email}`);
      }
      setModal(null);
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || "Erreur lors de l'envoi";
      if (/trop d'envois|rate/i.test(msg)) toast.warning("Envois trop rapprochés — réessayez dans quelques instants");
      else toast.error(msg);
    }
  };

  const doImport = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/participants/import", importForm.csv, { headers: { "Content-Type": "text/csv" } });
      setImportResult(data);
      toast.success(`${data.created} participants créés · ${data.skipped.length} ignorés`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const onCsvFile = (file) => {
    const reader = new FileReader();
    reader.onload = () => setImportForm({ csv: reader.result });
    reader.readAsText(file);
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer ${p.name} ? Ses inscriptions et carnets seront aussi supprimés. Action irréversible.`)) return;
    try { await api.delete(`/admin/participants/${p.id}`); toast.success("Participant supprimé"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const resetPwd = async (id, email) => {
    const custom = window.prompt(`Réinitialiser le mot de passe de ${email}.\n\nLaissez vide pour générer un mot de passe aléatoire, ou saisissez un nouveau mot de passe (min 6 caractères) :`, "");
    if (custom === null) return;
    try {
      const { data } = await api.post(`/admin/users/${id}/reset-password`, {
        new_password: custom.trim() || null,
        send_email: true,
        force_logout: true,
      });
      if (data.generated_password) {
        window.prompt("Nouveau mot de passe généré — copiez-le maintenant :", data.generated_password);
      }
      toast.success(data.email_sent ? "Mot de passe réinitialisé — email envoyé" : "Mot de passe réinitialisé");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-serif-display text-4xl font-semibold" style={{ color: "var(--pine)" }}>Participants</h1>
        <div className="flex gap-2">
          <button onClick={openImport} className="btn-ghost border" data-testid="participant-import-btn"><Upload size={16}/> Importer CSV</button>
          <button onClick={openCreate} className="btn-primary" data-testid="participant-new-btn"><Plus size={16}/> Ajouter</button>
        </div>
      </div>

      <div className="card-warm p-4 mt-6 flex items-center gap-3">
        <Search size={16} className="text-[var(--muted-text)]"/>
        <input placeholder="Rechercher par nom, email, téléphone…" className="input-field flex-1 !border-0 !p-0 !bg-transparent focus:!shadow-none" value={search} onChange={e => setSearch(e.target.value)} data-testid="participant-search" />
        <span className="text-xs text-[var(--muted-text)]" data-testid="participant-search-count">{filtered.length} / {items.length}</span>
      </div>

      <div className="mt-4 card-warm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[var(--sand)] text-left">
            <tr>
              <th className="p-4">Nom</th><th className="p-4">Email</th><th className="p-4">Téléphone</th><th className="p-4">Ville</th><th className="p-4">Activité</th><th className="p-4">Séances</th><th className="p-4">Résa</th><th className="p-4">Attribuer carnet</th><th className="p-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan="9" className="p-8 text-center text-[var(--muted-text)]">Aucun participant trouvé.</td></tr>}
            {filtered.map(p => (
              <tr key={p.id} className="border-t" data-testid={`admin-participant-row-${p.id}`}>
                <td className="p-4">
                  <button onClick={() => openHistory(p)} className="font-medium text-[var(--pine)] hover:underline text-left" data-testid={`participant-history-${p.id}`}>{p.name}</button>
                </td>
                <td className="p-4">{p.email}</td>
                <td className="p-4 text-[var(--muted-text)]">{p.phone || "—"}</td>
                <td className="p-4 text-[var(--muted-text)]">
                  {p.city ? <span className="whitespace-nowrap">{p.postal_code} {p.city}</span> : "—"}
                </td>
                <td className="p-4">
                  {p.activity ? <span className="badge-discipline badge-stage">{ACTIVITY_LABEL[p.activity] || p.activity}</span> : <span className="text-[var(--muted-text)]">—</span>}
                </td>
                <td className="p-4">
                  {p.unlimited_active ? (
                    <span className="badge-discipline badge-yoga" data-testid={`participant-unlimited-${p.id}`}>Illimité ∞</span>
                  ) : (
                    <span className="badge-discipline badge-yoga" data-testid={`participant-remaining-${p.id}`}>{p.sessions_remaining}</span>
                  )}
                </td>
                <td className="p-4">{p.bookings_count}</td>
                <td className="p-4">
                  <select className="input-field" onChange={e => { grantCarnet(p.email, e.target.value); e.target.value = ""; }} data-testid={`grant-carnet-${p.id}`}>
                    <option value="">— sélectionner —</option>
                    {carnets.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </td>
                <td className="p-4">
                  <div className="flex justify-end gap-1">
                    <button onClick={() => openHistory(p)} title="Historique" className="p-2 rounded-full text-[var(--forest)] hover:bg-emerald-50" data-testid={`participant-view-${p.id}`}><Eye size={16}/></button>
                    <button onClick={() => openEmail(p)} title="Envoyer un email" className="p-2 rounded-full text-[var(--pine)] hover:bg-slate-100" data-testid={`participant-email-${p.id}`}><Mail size={16}/></button>
                    <button onClick={() => openEdit(p)} title="Modifier" className="p-2 rounded-full text-[var(--pine)] hover:bg-slate-100" data-testid={`participant-edit-${p.id}`}><Pencil size={16}/></button>
                    <button onClick={() => resetPwd(p.id, p.email)} title="Réinitialiser mot de passe" className="p-2 rounded-full text-[var(--terracotta)] hover:bg-orange-50" data-testid={`participant-reset-pwd-${p.id}`}><Repeat2 size={16}/></button>
                    <button onClick={() => remove(p)} title="Supprimer" className="p-2 rounded-full text-red-600 hover:bg-red-50" data-testid={`participant-delete-${p.id}`}><Trash2 size={16}/></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (modal.mode === "create" || modal.mode === "edit") && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="participant-modal">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="p-5 border-b flex items-center justify-between">
              <h2 className="font-serif-display text-2xl" style={{ color: "var(--pine)" }}>{modal.mode === "create" ? "Ajouter un participant" : "Modifier le participant"}</h2>
              <button onClick={() => setModal(null)} className="p-1 hover:bg-slate-100 rounded-full" data-testid="participant-modal-close"><X size={18}/></button>
            </div>
            <form onSubmit={submit} className="p-5 space-y-4 max-h-[80vh] overflow-auto">
              <input required placeholder="Nom complet" className="input-field" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="participant-form-name" />
              <input required type="email" placeholder="Email" className="input-field" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="participant-form-email" />
              <input placeholder="Téléphone (optionnel)" className="input-field" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} data-testid="participant-form-phone" />
              <input placeholder="Adresse" className="input-field" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} data-testid="participant-form-address" />
              <div className="grid grid-cols-3 gap-2">
                <input placeholder="CP" className="input-field" value={form.postal_code} onChange={e => setForm({ ...form, postal_code: e.target.value })} data-testid="participant-form-postal-code" />
                <input placeholder="Ville" className="input-field col-span-2" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} data-testid="participant-form-city" />
              </div>
              <select className="input-field" value={form.activity} onChange={e => setForm({ ...form, activity: e.target.value })} data-testid="participant-form-activity">
                {ACTIVITY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              {modal.mode === "create" && (
                <div>
                  <input type="text" placeholder="Mot de passe (vide = généré)" className="input-field" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} data-testid="participant-form-password" />
                  <div className="text-xs text-[var(--muted-text)] mt-1">Le mot de passe s'affichera dans le toast après création.</div>
                </div>
              )}
              <div className="flex gap-2 justify-end pt-2">
                <button type="button" onClick={() => setModal(null)} className="btn-ghost border">Annuler</button>
                <button type="submit" className="btn-primary" data-testid="participant-form-submit">{modal.mode === "create" ? "Créer" : "Enregistrer"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {modal && modal.mode === "email" && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="participant-email-modal">
          <div className="bg-white rounded-2xl w-full max-w-lg">
            <div className="p-5 border-b flex items-center justify-between">
              <h2 className="font-serif-display text-2xl" style={{ color: "var(--pine)" }}>Envoyer un email</h2>
              <button onClick={() => setModal(null)} className="p-1 hover:bg-slate-100 rounded-full"><X size={18}/></button>
            </div>
            <form onSubmit={sendEmail} className="p-5 space-y-4">
              <div className="text-sm text-[var(--muted-text)]">À : <strong className="text-[var(--pine)]">{modal.participant.name}</strong> ({modal.participant.email})</div>
              <input required placeholder="Sujet" className="input-field" value={emailForm.subject} onChange={e => setEmailForm({ ...emailForm, subject: e.target.value })} data-testid="participant-email-subject" />
              <textarea required rows="8" placeholder="Message (les sauts de ligne sont conservés)" className="input-field" value={emailForm.body} onChange={e => setEmailForm({ ...emailForm, body: e.target.value })} data-testid="participant-email-body" />

              <label className="block">
                <div className="flex items-center gap-2 cursor-pointer px-4 py-2 border-2 border-dashed border-[var(--forest)]/40 rounded-xl hover:border-[var(--forest)] hover:bg-[var(--forest-light)]/30 transition text-sm">
                  <Paperclip size={14} className="text-[var(--forest)]"/>
                  <span>Ajouter des pièces jointes</span>
                </div>
                <input type="file" multiple className="hidden" onChange={e => { setEmailFiles(prev => [...prev, ...Array.from(e.target.files || [])]); e.target.value = ""; }} data-testid="participant-email-attach-input" />
              </label>
              {emailFiles.length > 0 && (
                <div className="space-y-1">
                  {emailFiles.map((f, i) => (
                    <div key={i} className="flex items-center justify-between p-2 rounded bg-[var(--sand)] text-xs">
                      <span className="truncate flex items-center gap-1"><Paperclip size={12}/> {f.name} <span className="text-[var(--muted-text)]">({(f.size/1024).toFixed(1)} Ko)</span></span>
                      <button type="button" onClick={() => setEmailFiles(prev => prev.filter((_, j) => j !== i))} className="text-red-600 p-1 hover:bg-red-50 rounded-full"><X size={12}/></button>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-2 justify-end pt-2">
                <button type="button" onClick={() => setModal(null)} className="btn-ghost border">Annuler</button>
                <button type="submit" className="btn-primary" data-testid="participant-email-submit"><Send size={14}/> Envoyer</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {modal && modal.mode === "import" && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="participant-import-modal">
          <div className="bg-white rounded-2xl w-full max-w-2xl">
            <div className="p-5 border-b flex items-center justify-between">
              <h2 className="font-serif-display text-2xl" style={{ color: "var(--pine)" }}>Importer des participants (CSV)</h2>
              <button onClick={() => setModal(null)} className="p-1 hover:bg-slate-100 rounded-full"><X size={18}/></button>
            </div>
            <form onSubmit={doImport} className="p-5 space-y-4">
              <div className="text-sm text-[var(--muted-text)]">
                Format attendu : <code>name,email,phone</code> (ou <code>nom;email;telephone</code>). La 1<sup>ère</sup> ligne peut être une en-tête. Les emails déjà existants sont ignorés. Un mot de passe temporaire est généré pour chaque nouveau participant.
              </div>
              <input type="file" accept=".csv,text/csv" onChange={e => e.target.files[0] && onCsvFile(e.target.files[0])} className="input-field" data-testid="participant-import-file" />
              <textarea rows="6" placeholder="Ou collez le CSV ici…" className="input-field font-mono text-xs" value={importForm.csv} onChange={e => setImportForm({ csv: e.target.value })} data-testid="participant-import-csv" />
              <div className="flex gap-2 justify-end">
                <button type="button" onClick={() => setModal(null)} className="btn-ghost border">Fermer</button>
                <button type="submit" disabled={!importForm.csv.trim()} className="btn-primary disabled:opacity-40" data-testid="participant-import-submit"><Upload size={14}/> Importer</button>
              </div>
              {importResult && (
                <div className="mt-4 p-4 rounded-lg bg-[var(--forest-light)] text-sm" data-testid="participant-import-result">
                  <div className="font-semibold text-[var(--pine)]">✓ {importResult.created} participants créés · {importResult.skipped.length} ignorés</div>
                  {importResult.generated?.length > 0 && (
                    <div className="mt-3">
                      <div className="text-xs text-[var(--muted-text)] mb-1">Mots de passe temporaires générés — communiquez-les aux participants :</div>
                      <div className="max-h-48 overflow-auto text-xs font-mono bg-white rounded p-2 border">
                        {importResult.generated.map(g => <div key={g.email}>{g.email} — {g.password}</div>)}
                      </div>
                    </div>
                  )}
                  {importResult.skipped?.length > 0 && (
                    <div className="mt-3 text-xs">
                      <div className="text-[var(--muted-text)] mb-1">Ignorés :</div>
                      <div className="max-h-24 overflow-auto">{importResult.skipped.map((s, i) => <div key={i}>· {s.email} — {s.reason}</div>)}</div>
                    </div>
                  )}
                </div>
              )}
            </form>
          </div>
        </div>
      )}

      {modal && modal.mode === "history" && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="participant-history-modal">
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-auto">
            <div className="p-5 border-b flex items-center justify-between sticky top-0 bg-white">
              <div>
                <h2 className="font-serif-display text-2xl" style={{ color: "var(--pine)" }}>{modal.participant.name}</h2>
                <div className="text-sm text-[var(--muted-text)]">{modal.participant.email} · {modal.participant.phone || "—"}</div>
              </div>
              <button onClick={() => setModal(null)} className="p-1 hover:bg-slate-100 rounded-full"><X size={18}/></button>
            </div>
            <div className="p-5 space-y-6">
              {!history && <div className="text-center text-[var(--muted-text)] py-8">Chargement…</div>}
              {history && (
                <>
                  <section>
                    <h3 className="overline mb-3">Carnets ({history.subscriptions.length})</h3>
                    {history.subscriptions.length === 0 && <div className="text-sm text-[var(--muted-text)]">Aucun carnet.</div>}
                    <div className="space-y-2">
                      {history.subscriptions.map(s => (
                        <div key={s.id} className="p-3 rounded-lg bg-[var(--sand)] text-sm flex items-center justify-between">
                          <div><strong>{s.carnet_name}</strong> — {s.sessions_remaining}/{s.sessions_total} restantes</div>
                          <div className="text-xs text-[var(--muted-text)]">{new Date(s.created_at).toLocaleDateString("fr-FR")} · {s.price_eur}€</div>
                        </div>
                      ))}
                    </div>
                  </section>

                  <section>
                    <h3 className="overline mb-3">Réservations ({history.bookings.length})</h3>
                    {history.bookings.length === 0 && <div className="text-sm text-[var(--muted-text)]">Aucune réservation.</div>}
                    <div className="space-y-2">
                      {history.bookings.map(b => (
                        <div key={b.id} className="p-3 rounded-lg bg-[var(--sand)] text-sm flex items-center justify-between gap-3 flex-wrap">
                          <div>
                            <div><strong>{b.session?.title || "Séance supprimée"}</strong></div>
                            <div className="text-xs text-[var(--muted-text)]">
                              {b.session?.start_at ? new Date(b.session.start_at).toLocaleString("fr-FR") : ""} · {b.session?.location || ""}
                            </div>
                          </div>
                          <div className="flex gap-2 text-xs">
                            {b.cancelled ? <span className="badge-discipline badge-marche">Annulé</span> :
                              b.attended ? <span className="badge-discipline badge-yoga">Présent</span> :
                              <span className="badge-discipline badge-stage">À venir</span>}
                            {b.paid ? <span className="badge-discipline badge-yoga">Payé ({b.payment_method})</span> : <span className="badge-discipline badge-marche">Non payé</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>

                  <section>
                    <h3 className="overline mb-3">Paiements ({history.payments.length})</h3>
                    {history.payments.length === 0 && <div className="text-sm text-[var(--muted-text)]">Aucun paiement.</div>}
                    <div className="space-y-2">
                      {history.payments.map(t => (
                        <div key={t.id} className="p-3 rounded-lg bg-[var(--sand)] text-sm flex items-center justify-between">
                          <div><strong>{t.amount_eur}€</strong> — {t.type} ({t.method})</div>
                          <div className="text-xs">
                            <span className={`badge-discipline ${t.status === "paid" ? "badge-yoga" : "badge-marche"}`}>{t.status}</span>
                            <span className="text-[var(--muted-text)] ml-2">{new Date(t.created_at).toLocaleDateString("fr-FR")}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Prospects() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [view, setView] = useState("list"); // "list" | "map"
  const [modal, setModal] = useState(null); // { mode, prospect? }
  const emptyForm = { first_name: "", last_name: "", email: "", phone: "", address: "", postal_code: "", city: "", activity: "other", status: "nouveau", notes: "", next_contact_at: "" };
  const [form, setForm] = useState(emptyForm);
  const [emailForm, setEmailForm] = useState({ subject: "", body: "" });
  const [emailFiles, setEmailFiles] = useState([]);
  const [importForm, setImportForm] = useState({ csv: "" });
  const [importResult, setImportResult] = useState(null);
  const [convertForm, setConvertForm] = useState({ password: "" });

  const load = () => {
    api.get("/admin/prospects").then(r => setItems(r.data));
    api.get("/admin/prospects/stats").then(r => setStats(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const filtered = items.filter(p => {
    if (statusFilter && p.status !== statusFilter) return false;
    if (overdueOnly && !isOverdue(p)) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (p.first_name || "").toLowerCase().includes(q)
      || (p.last_name || "").toLowerCase().includes(q)
      || (p.email || "").toLowerCase().includes(q)
      || (p.phone || "").toLowerCase().includes(q)
      || (p.city || "").toLowerCase().includes(q);
  });

  const openCreate = () => { setForm(emptyForm); setModal({ mode: "create" }); };
  const openEdit = (p) => {
    setForm({
      first_name: p.first_name || "", last_name: p.last_name || "", email: p.email || "",
      phone: p.phone || "", address: p.address || "", postal_code: p.postal_code || "",
      city: p.city || "", activity: p.activity || "other", status: p.status || "nouveau", notes: p.notes || "",
      next_contact_at: (p.next_contact_at || "").slice(0, 10),
    });
    setModal({ mode: "edit", prospect: p });
  };
  const openEmail = (p) => { setEmailForm({ subject: "", body: "" }); setEmailFiles([]); setModal({ mode: "email", prospect: p }); };
  const openImport = () => { setImportForm({ csv: "" }); setImportResult(null); setModal({ mode: "import" }); };
  const openConvert = (p) => { setConvertForm({ password: "" }); setModal({ mode: "convert", prospect: p }); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, email: form.email || null };
      if (modal.mode === "create") {
        await api.post("/admin/prospects", payload);
        toast.success("Prospect ajouté");
      } else {
        await api.put(`/admin/prospects/${modal.prospect.id}`, payload);
        toast.success("Prospect mis à jour");
      }
      setModal(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer ${p.first_name} ${p.last_name} ? Action irréversible.`)) return;
    try { await api.delete(`/admin/prospects/${p.id}`); toast.success("Prospect supprimé"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doConvert = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/admin/prospects/${modal.prospect.id}/convert`, { password: convertForm.password || undefined });
      if (data.merged) {
        const filled = (data.filled_fields || []).length;
        toast.success(`Prospect fusionné avec le compte existant (${data.email})${filled ? ` · ${filled} champ(s) profil complétés` : ""}`, { duration: 10000 });
      } else if (data.generated_password) {
        toast.success(`Compte créé pour ${data.email}. Mot de passe : ${data.generated_password}`, { duration: 15000 });
      } else {
        toast.success(`Compte créé pour ${data.email}`);
      }
      setModal(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sendEmail = async (e) => {
    e.preventDefault();
    try {
      const html = `<div style="font-family:Arial,sans-serif;padding:24px">${emailForm.body.replace(/\n/g, "<br/>")}</div>`;
      const fd = new FormData();
      fd.append("subject", emailForm.subject);
      fd.append("html", html);
      emailFiles.forEach(f => fd.append("files", f, f.name));
      const { data } = await api.post(`/admin/prospects/${modal.prospect.id}/email`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.attachments_ignored > 0) {
        toast.warning(`Email envoyé à ${modal.prospect.email} — ${data.attachments_ignored} pièce(s) jointe(s) non prise(s) en charge (fonctionnalité indisponible pour le moment)`);
      } else {
        toast.success(`Email envoyé à ${modal.prospect.email}`);
      }
      setModal(null);
      load();
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || "Erreur lors de l'envoi";
      if (/trop d'envois|rate/i.test(msg)) toast.warning("Envois trop rapprochés — réessayez dans quelques instants");
      else toast.error(msg);
    }
  };

  const doImport = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/prospects/import", importForm.csv, { headers: { "Content-Type": "text/csv" } });
      setImportResult(data);
      toast.success(`${data.created} prospects créés · ${data.skipped.length} ignorés`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const onCsvFile = (file) => {
    const reader = new FileReader();
    reader.onload = () => setImportForm({ csv: reader.result });
    reader.readAsText(file);
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/prospects/export.csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "prospects.csv"; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-serif-display text-4xl font-semibold" style={{ color: "var(--pine)" }}>Prospects</h1>
        <div className="flex gap-2 flex-wrap items-center">
          <div className="flex gap-1 p-1 rounded-full bg-white border border-slate-200" data-testid="prospect-view-toggle">
            <button onClick={() => setView("list")} className={`px-3 py-1.5 rounded-full text-sm inline-flex items-center gap-1.5 ${view === "list" ? "bg-[var(--pine)] text-white" : "text-[var(--muted-text)]"}`} data-testid="prospect-view-list">
              <LayoutGrid size={14}/> Liste
            </button>
            <button onClick={() => setView("map")} className={`px-3 py-1.5 rounded-full text-sm inline-flex items-center gap-1.5 ${view === "map" ? "bg-[var(--pine)] text-white" : "text-[var(--muted-text)]"}`} data-testid="prospect-view-map">
              <MapIcon size={14}/> Carte
            </button>
          </div>
          <button onClick={async () => {
            try {
              const { data } = await api.post("/admin/prospects/send-followups-now");
              if (data.skipped) toast.info("Aucune relance à envoyer aujourd'hui.");
              else toast.success(`Digest envoyé à ${data.emails_sent}/${data.admins} admin(s) · ${data.due} prospect(s)`);
            } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
          }} className="btn-ghost border" data-testid="prospect-followups-now-btn"><Bell size={16}/> Envoyer relances</button>
          <button onClick={exportCsv} className="btn-ghost border" data-testid="prospect-export-btn"><Download size={16}/> Exporter CSV</button>
          <button onClick={openImport} className="btn-ghost border" data-testid="prospect-import-btn"><Upload size={16}/> Importer CSV</button>
          <button onClick={openCreate} className="btn-primary" data-testid="prospect-new-btn"><Plus size={16}/> Ajouter</button>
        </div>
      </div>
      <p className="mt-2 text-[var(--muted-text)]">Suivez vos contacts intéressés avant qu'ils ne deviennent participants.</p>

      {stats && (
        <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="prospect-stats">
          <div className="card-warm p-4" data-testid="prospect-stat-total">
            <div className="overline">Total</div>
            <div className="mt-1 font-serif-display text-3xl font-semibold" style={{ color: "var(--pine)" }}>{stats.total}</div>
          </div>
          <div className="card-warm p-4" data-testid="prospect-stat-nouveaux">
            <div className="overline">Nouveaux</div>
            <div className="mt-1 font-serif-display text-3xl font-semibold" style={{ color: "var(--pine)" }}>{stats.by_status?.nouveau || 0}</div>
          </div>
          <div className="card-warm p-4" data-testid="prospect-stat-interesses">
            <div className="overline">Intéressés</div>
            <div className="mt-1 font-serif-display text-3xl font-semibold" style={{ color: "var(--forest)" }}>{stats.by_status?.interesse || 0}</div>
          </div>
          <div className="card-warm p-4" data-testid="prospect-stat-convertis">
            <div className="overline">Convertis</div>
            <div className="mt-1 font-serif-display text-3xl font-semibold" style={{ color: "var(--forest)" }}>{stats.by_status?.converti || 0}</div>
          </div>
          <div className="card-warm p-4" data-testid="prospect-stat-conversion">
            <div className="overline">Taux conv.</div>
            <div className="mt-1 font-serif-display text-3xl font-semibold" style={{ color: "var(--terracotta)" }}>{stats.conversion_rate_pct} %</div>
          </div>
        </div>
      )}

      {stats && stats.overdue_followups > 0 && (
        <button onClick={() => setOverdueOnly(v => !v)} className={`mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm border transition ${overdueOnly ? "bg-[var(--terracotta)] text-white border-[var(--terracotta)]" : "bg-white text-[var(--terracotta)] border-[var(--terracotta)]/40 hover:bg-[var(--terracotta)]/10"}`} data-testid="prospect-overdue-toggle">
          <Clock size={14}/> {stats.overdue_followups} relance(s) en retard{overdueOnly ? " · afficher tout" : " · filtrer"}
        </button>
      )}

      {stats && Object.values(stats.by_activity || {}).some(v => v > 0) && (
        <div className="mt-4 flex flex-wrap gap-2 text-xs" data-testid="prospect-activity-breakdown">
          {ACTIVITY_OPTIONS.filter(o => o.value).map(o => {
            const n = stats.by_activity?.[o.value] || 0;
            if (!n) return null;
            return <span key={o.value} className="badge-discipline badge-stage">{o.label} · {n}</span>;
          })}
        </div>
      )}

      <div className="card-warm p-4 mt-6 flex items-center gap-3 flex-wrap">
        <Search size={16} className="text-[var(--muted-text)]"/>
        <input placeholder="Rechercher (nom, email, ville, téléphone…)" className="input-field flex-1 min-w-[240px] !border-0 !p-0 !bg-transparent focus:!shadow-none" value={search} onChange={e => setSearch(e.target.value)} data-testid="prospect-search" />
        <select className="input-field !py-2 !w-auto" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-testid="prospect-status-filter">
          <option value="">Tous les statuts</option>
          {PROSPECT_STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <span className="text-xs text-[var(--muted-text)]" data-testid="prospect-count">{filtered.length} / {items.length}</span>
      </div>

      <div className="mt-4 card-warm overflow-x-auto" style={{ display: view === "list" ? "block" : "none" }}>
        <table className="w-full text-sm">
          <thead className="bg-[var(--sand)] text-left">
            <tr>
              <th className="p-4">Nom</th><th className="p-4">Prénom</th><th className="p-4">Email</th><th className="p-4">Téléphone</th><th className="p-4">Ville</th><th className="p-4">Activité</th><th className="p-4">Statut</th><th className="p-4">Prochain contact</th><th className="p-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan="9" className="p-8 text-center text-[var(--muted-text)]">Aucun prospect.</td></tr>}
            {filtered.map(p => {
              const st = PROSPECT_STATUS_MAP[p.status] || PROSPECT_STATUS_OPTIONS[0];
              const isConverted = p.status === "converti";
              const overdue = isOverdue(p);
              return (
                <tr key={p.id} className="border-t" data-testid={`prospect-row-${p.id}`}>
                  <td className="p-4 font-medium">{p.last_name || "—"}</td>
                  <td className="p-4">{p.first_name || "—"}</td>
                  <td className="p-4">{p.email || <span className="text-[var(--muted-text)]">—</span>}</td>
                  <td className="p-4 text-[var(--muted-text)]">{p.phone || "—"}</td>
                  <td className="p-4 text-[var(--muted-text)] whitespace-nowrap">{p.city ? `${p.postal_code || ""} ${p.city}`.trim() : "—"}</td>
                  <td className="p-4"><span className="badge-discipline badge-stage">{ACTIVITY_LABEL[p.activity] || p.activity}</span></td>
                  <td className="p-4"><span className={`badge-discipline ${st.cls}`}>{st.label}</span></td>
                  <td className="p-4 whitespace-nowrap">
                    {p.next_contact_at ? (
                      <span className={`text-xs inline-flex items-center gap-1 ${overdue ? "text-red-600 font-semibold" : "text-[var(--muted-text)]"}`} data-testid={`prospect-next-${p.id}`}>
                        <Clock size={12}/> {new Date(p.next_contact_at).toLocaleDateString("fr-FR")}{overdue && " · en retard"}
                      </span>
                    ) : <span className="text-[var(--muted-text)]">—</span>}
                  </td>
                  <td className="p-4">
                    <div className="flex justify-end gap-1">
                      {!isConverted && p.email && (
                        <button onClick={() => openConvert(p)} title="Convertir en participant" className="p-2 rounded-full text-emerald-700 hover:bg-emerald-50" data-testid={`prospect-convert-${p.id}`}><UserPlus size={16}/></button>
                      )}
                      {p.email && (
                        <button onClick={() => openEmail(p)} title="Envoyer un email" className="p-2 rounded-full text-[var(--pine)] hover:bg-slate-100" data-testid={`prospect-email-${p.id}`}><Mail size={16}/></button>
                      )}
                      <button onClick={() => openEdit(p)} title="Modifier" className="p-2 rounded-full text-[var(--pine)] hover:bg-slate-100" data-testid={`prospect-edit-${p.id}`}><Pencil size={16}/></button>
                      <button onClick={() => remove(p)} title="Supprimer" className="p-2 rounded-full text-red-600 hover:bg-red-50" data-testid={`prospect-delete-${p.id}`}><Trash2 size={16}/></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {view === "map" && (
        <div className="mt-4" data-testid="prospect-map-container">
          <ProspectsMap prospects={filtered} height="560px" />
          <div className="mt-3 text-xs text-[var(--muted-text)] flex flex-wrap items-center gap-3">
            <span>Légende :</span>
            {PROSPECT_STATUS_OPTIONS.map(o => (
              <span key={o.value} className="inline-flex items-center gap-1">
                <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: {nouveau:"#4A90A4", contacte:"#C46A4B", interesse:"#5E8B6E", converti:"#2E5A44", perdu:"#9AA5A0"}[o.value] }} />
                {o.label}
              </span>
            ))}
            <button className="ml-auto text-xs underline text-[var(--pine)] hover:opacity-70" onClick={async () => {
              try {
                const { data } = await api.post("/admin/prospects/geocode-all");
                toast.success(`Géocodage : ${data.updated} mis à jour, ${data.skipped} ignorés`);
                load();
              } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
            }} data-testid="prospect-geocode-all">Géocoder les prospects sans coordonnées</button>
          </div>
        </div>
      )}

      {modal && (modal.mode === "create" || modal.mode === "edit") && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="prospect-modal">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-auto">
            <div className="p-5 border-b flex items-center justify-between sticky top-0 bg-white">
              <h2 className="font-serif-display text-2xl" style={{ color: "var(--pine)" }}>{modal.mode === "create" ? "Ajouter un prospect" : "Modifier le prospect"}</h2>
              <button onClick={() => setModal(null)} className="p-1 hover:bg-slate-100 rounded-full" data-testid="prospect-modal-close"><X size={18}/></button>
            </div>
            <form onSubmit={submit} className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <input required placeholder="Nom" className="input-field" value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} data-testid="prospect-form-last-name" />
                <input required placeholder="Prénom" className="input-field" value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} data-testid="prospect-form-first-name" />
              </div>
              <input type="email" placeholder="Email (optionnel)" className="input-field" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="prospect-form-email" />
              <input placeholder="Téléphone" className="input-field" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} data-testid="prospect-form-phone" />
              <input placeholder="Adresse" className="input-field" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} data-testid="prospect-form-address" />
              <div className="grid grid-cols-3 gap-3">
                <input placeholder="CP" className="input-field" value={form.postal_code} onChange={e => setForm({ ...form, postal_code: e.target.value })} data-testid="prospect-form-postal-code" />
                <input placeholder="Ville" className="input-field col-span-2" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} data-testid="prospect-form-city" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[var(--muted-text)] block mb-1">Activité souhaitée</label>
                  <select className="input-field" value={form.activity} onChange={e => setForm({ ...form, activity: e.target.value })} data-testid="prospect-form-activity">
                    {ACTIVITY_OPTIONS.filter(o => o.value).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-[var(--muted-text)] block mb-1">Statut</label>
                  <select className="input-field" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} data-testid="prospect-form-status">
                    {PROSPECT_STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
              </div>
              <textarea rows="3" placeholder="Notes internes" className="input-field" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} data-testid="prospect-form-notes" />
              <div>
                <label className="text-xs text-[var(--muted-text)] block mb-1">Prochain contact (relance)</label>
                <input type="date" className="input-field" value={form.next_contact_at} onChange={e => setForm({ ...form, next_contact_at: e.target.value })} data-testid="prospect-form-next-contact" />
                <p className="text-xs text-[var(--muted-text)] mt-1">Un badge « en retard » apparaît si la date est passée et que le prospect n'est pas converti ou perdu.</p>
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <button type="button" onClick={() => setModal(null)} className="btn-ghost border">Annuler</button>
                <button type="submit" className="btn-primary" data-testid="prospect-form-submit">{modal.mode === "create" ? "Créer" : "Enregistrer"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {modal && modal.mode === "convert" && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="prospect-convert-modal">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="p-5 border-b flex items-center justify-between">
              <h2 className="font-serif-display text-2xl" style={{ color: "var(--pine)" }}>Convertir en participant</h2>
              <button onClick={() => setModal(null)} className="p-1 hover:bg-slate-100 rounded-full"><X size={18}/></button>
            </div>
            <form onSubmit={doConvert} className="p-5 space-y-4">
              <div className="text-sm text-[var(--muted-text)]">
                Un compte participant sera créé pour <strong className="text-[var(--pine)]">{modal.prospect.first_name} {modal.prospect.last_name}</strong> ({modal.prospect.email}).
                <br/>Si un compte existe déjà avec cet email, il sera <strong>fusionné</strong> et complété avec les infos du prospect.
                Le prospect passera automatiquement au statut « Converti ».
              </div>
              <input type="text" placeholder="Mot de passe (vide = généré)" className="input-field" value={convertForm.password} onChange={e => setConvertForm({ password: e.target.value })} data-testid="prospect-convert-password" />
              <div className="flex gap-2 justify-end">
                <button type="button" onClick={() => setModal(null)} className="btn-ghost border">Annuler</button>
                <button type="submit" className="btn-primary" data-testid="prospect-convert-submit"><Repeat2 size={14}/> Convertir</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {modal && modal.mode === "email" && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="prospect-email-modal">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-auto">
            <div className="p-5 border-b flex items-center justify-between sticky top-0 bg-white">
              <h2 className="font-serif-display text-2xl" style={{ color: "var(--pine)" }}>Envoyer un email</h2>
              <button onClick={() => setModal(null)} className="p-1 hover:bg-slate-100 rounded-full"><X size={18}/></button>
            </div>
            <form onSubmit={sendEmail} className="p-5 space-y-4">
              <div className="text-sm text-[var(--muted-text)]">À : <strong className="text-[var(--pine)]">{modal.prospect.first_name} {modal.prospect.last_name}</strong> ({modal.prospect.email})</div>
              <input required placeholder="Sujet" className="input-field" value={emailForm.subject} onChange={e => setEmailForm({ ...emailForm, subject: e.target.value })} data-testid="prospect-email-subject" />
              <textarea required rows="8" placeholder="Message (les sauts de ligne sont conservés)" className="input-field" value={emailForm.body} onChange={e => setEmailForm({ ...emailForm, body: e.target.value })} data-testid="prospect-email-body" />
              <label className="block">
                <div className="flex items-center gap-2 cursor-pointer px-4 py-2 border-2 border-dashed border-[var(--forest)]/40 rounded-xl hover:border-[var(--forest)] hover:bg-[var(--forest-light)]/30 transition text-sm">
                  <Paperclip size={14} className="text-[var(--forest)]"/>
                  <span>Ajouter des pièces jointes</span>
                </div>
                <input type="file" multiple className="hidden" onChange={e => { setEmailFiles(prev => [...prev, ...Array.from(e.target.files || [])]); e.target.value = ""; }} data-testid="prospect-email-attach-input" />
              </label>
              {emailFiles.length > 0 && (
                <div className="space-y-1">
                  {emailFiles.map((f, i) => (
                    <div key={i} className="flex items-center justify-between p-2 rounded bg-[var(--sand)] text-xs">
                      <span className="truncate flex items-center gap-1"><Paperclip size={12}/> {f.name} <span className="text-[var(--muted-text)]">({(f.size/1024).toFixed(1)} Ko)</span></span>
                      <button type="button" onClick={() => setEmailFiles(prev => prev.filter((_, j) => j !== i))} className="text-red-600 p-1 hover:bg-red-50 rounded-full"><X size={12}/></button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-2 justify-end pt-2">
                <button type="button" onClick={() => setModal(null)} className="btn-ghost border">Annuler</button>
                <button type="submit" className="btn-primary" data-testid="prospect-email-submit"><Send size={14}/> Envoyer</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {modal && modal.mode === "import" && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="prospect-import-modal">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-auto">
            <div className="p-5 border-b flex items-center justify-between sticky top-0 bg-white">
              <h2 className="font-serif-display text-2xl" style={{ color: "var(--pine)" }}>Importer des prospects (CSV)</h2>
              <button onClick={() => setModal(null)} className="p-1 hover:bg-slate-100 rounded-full"><X size={18}/></button>
            </div>
            <form onSubmit={doImport} className="p-5 space-y-4">
              <div className="text-sm text-[var(--muted-text)]">
                Format attendu : <code>Nom;Prénom;Adresse;CP;Ville;Téléphone;Email;Activité</code>. La 1<sup>ère</sup> ligne peut être une en-tête. Les emails déjà existants sont ignorés.
              </div>
              <input type="file" accept=".csv,text/csv" onChange={e => e.target.files[0] && onCsvFile(e.target.files[0])} className="input-field" data-testid="prospect-import-file" />
              <textarea rows="6" placeholder="Ou collez le CSV ici…" className="input-field font-mono text-xs" value={importForm.csv} onChange={e => setImportForm({ csv: e.target.value })} data-testid="prospect-import-csv" />
              <div className="flex gap-2 justify-end">
                <button type="button" onClick={() => setModal(null)} className="btn-ghost border">Fermer</button>
                <button type="submit" disabled={!importForm.csv.trim()} className="btn-primary disabled:opacity-40" data-testid="prospect-import-submit"><Upload size={14}/> Importer</button>
              </div>
              {importResult && (
                <div className="mt-4 p-4 rounded-lg bg-[var(--forest-light)] text-sm" data-testid="prospect-import-result">
                  <div className="font-semibold text-[var(--pine)]">✓ {importResult.created} prospects créés · {importResult.skipped.length} ignorés</div>
                  {importResult.skipped?.length > 0 && (
                    <div className="mt-2 text-xs">
                      <div className="max-h-24 overflow-auto">{importResult.skipped.map((s, i) => <div key={i}>· {s.email || "(sans email)"} — {s.reason}</div>)}</div>
                    </div>
                  )}
                </div>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function Payments() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // tx object or null
  const [form, setForm] = useState({ amount_eur: "", method: "", note: "" });
  const [exportMonth, setExportMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const load = () => api.get("/admin/payments").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const exportCsv = async () => {
    try {
      const res = await api.get(`/admin/payments/export.csv?month=${exportMonth}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `paiements-${exportMonth}.csv`; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export CSV téléchargé");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({ amount_eur: t.amount_eur ?? "", method: t.method ?? "", note: t.note ?? "" });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/admin/payments/${editing.id}`, {
        amount_eur: form.amount_eur === "" ? null : Number(form.amount_eur),
        method: form.method || null,
        note: form.note,
      });
      toast.success("Paiement mis à jour");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const validate = async (id) => {
    try {
      await api.post(`/admin/payments/${id}/validate`);
      toast.success("Paiement validé");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette transaction ? Cette action est irréversible.")) return;
    try {
      await api.delete(`/admin/payments/${id}`);
      toast.success("Transaction supprimée");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-serif-display text-4xl font-semibold" style={{ color: "var(--pine)" }}>Suivi des paiements</h1>
        <div className="flex items-center gap-2">
          <label className="text-xs text-[var(--muted-text)]">Export comptable :</label>
          <input type="month" value={exportMonth} onChange={e => setExportMonth(e.target.value)} className="input-field !py-2 !w-auto" data-testid="payment-export-month" />
          <button onClick={exportCsv} className="btn-ghost border inline-flex items-center gap-1.5" data-testid="payment-export-btn">
            <Download size={14}/> Export CSV
          </button>
        </div>
      </div>
      <div className="mt-6 card-warm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[var(--sand)] text-left"><tr><th className="p-4">Date</th><th className="p-4">Type</th><th className="p-4">Montant</th><th className="p-4">Moyen</th><th className="p-4">Statut</th><th className="p-4 text-right">Actions</th></tr></thead>
          <tbody>
            {items.map(t => {
              const isPaid = t.status === "paid" || t.payment_status === "paid";
              return (
                <tr key={t.id} className="border-t" data-testid={`payment-row-${t.id}`}>
                  <td className="p-4">{t.created_at && new Date(t.created_at).toLocaleDateString("fr-FR")}</td>
                  <td className="p-4">{t.type}</td>
                  <td className="p-4 font-medium">{t.amount_eur} €</td>
                  <td className="p-4">{t.method}</td>
                  <td className="p-4"><span className={`badge-discipline ${isPaid ? "badge-yoga" : "badge-marche"}`}>{t.status}</span></td>
                  <td className="p-4">
                    <div className="flex justify-end gap-1">
                      {!isPaid && (
                        <button onClick={() => validate(t.id)} title="Valider comme payé" className="p-2 rounded-full text-emerald-700 hover:bg-emerald-50" data-testid={`payment-validate-${t.id}`}>
                          <Check size={16}/>
                        </button>
                      )}
                      <button onClick={() => openEdit(t)} title="Modifier" className="p-2 rounded-full text-[var(--pine)] hover:bg-slate-100" data-testid={`payment-edit-${t.id}`}>
                        <Pencil size={16}/>
                      </button>
                      <button onClick={() => remove(t.id)} title="Supprimer" className="p-2 rounded-full text-red-600 hover:bg-red-50" data-testid={`payment-delete-${t.id}`}>
                        <Trash2 size={16}/>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="payment-edit-modal">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="p-5 border-b flex items-center justify-between">
              <h2 className="font-serif-display text-2xl" style={{ color: "var(--pine)" }}>Modifier le paiement</h2>
              <button onClick={() => setEditing(null)} className="p-1 hover:bg-slate-100 rounded-full" data-testid="payment-edit-close"><X size={18}/></button>
            </div>
            <form onSubmit={saveEdit} className="p-5 space-y-4">
              <div>
                <label className="text-xs text-[var(--muted-text)] block mb-1">Montant (€)</label>
                <input type="number" step="0.01" required className="input-field" value={form.amount_eur} onChange={e => setForm({ ...form, amount_eur: e.target.value })} data-testid="payment-edit-amount" />
              </div>
              <div>
                <label className="text-xs text-[var(--muted-text)] block mb-1">Moyen de paiement</label>
                <select className="input-field" value={form.method} onChange={e => setForm({ ...form, method: e.target.value })} data-testid="payment-edit-method">
                  <option value="cash">Espèces</option>
                  <option value="qr">QR Code (Bancontact)</option>
                  <option value="transfer">Virement</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-[var(--muted-text)] block mb-1">Note</label>
                <textarea rows="3" className="input-field" value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} data-testid="payment-edit-note" />
              </div>
              <div className="flex gap-2 justify-end">
                <button type="button" onClick={() => setEditing(null)} className="btn-ghost border">Annuler</button>
                <button type="submit" className="btn-primary" data-testid="payment-edit-submit">Enregistrer</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function SignatureSettings() {
  const [cfg, setCfg] = useState(null);
  const [preview, setPreview] = useState("");
  const [busy, setBusy] = useState(false);
  const [coachEmail, setCoachEmail] = useState("");
  const [coachDefault, setCoachDefault] = useState("");
  const [coachBusy, setCoachBusy] = useState(false);

  const loadAll = async () => {
    const { data } = await api.get("/admin/settings/signature");
    setCfg(data);
    const p = await api.get("/admin/settings/signature/preview");
    setPreview(p.data.html);
    const c = await api.get("/admin/settings/coach-email");
    setCoachEmail(c.data.email || "");
    setCoachDefault(c.data.default || "");
  };
  useEffect(() => { loadAll(); }, []);

  const saveCoach = async (e) => {
    e.preventDefault();
    setCoachBusy(true);
    try {
      const { data } = await api.put("/admin/settings/coach-email", { email: coachEmail });
      toast.success(`Adresse coach mise à jour : ${data.email}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setCoachBusy(false); }
  };

  const sendDigestNow = async () => {
    try {
      const { data } = await api.post("/admin/coach-digest/send-now");
      if (data.skipped) toast.info(`Envoi ignoré : ${data.reason}`);
      else toast.success(`Récap envoyé (${data.sessions} séance(s) du jour)`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/admin/settings/signature", cfg);
      toast.success("Signature enregistrée");
      const p = await api.get("/admin/settings/signature/preview");
      setPreview(p.data.html);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!cfg) return null;
  return (
    <section className="mt-12 card-warm p-6 max-w-4xl" data-testid="signature-settings">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-6">
        <div>
          <h2 className="font-serif-display text-2xl font-semibold" style={{ color: "var(--pine)" }}>Notifications coach</h2>
          <p className="text-sm text-[var(--muted-text)] mt-1">Adresse email qui reçoit les alertes automatiques (inscription, réservation, annulation, séance complète, récap quotidien).</p>
        </div>
      </div>
      <form onSubmit={saveCoach} className="flex items-end gap-3 flex-wrap" data-testid="coach-email-form">
        <div className="flex-1 min-w-[280px]">
          <label className="text-xs text-[var(--muted-text)] block mb-1">Adresse coach</label>
          <input type="email" className="input-field" value={coachEmail} onChange={e => setCoachEmail(e.target.value)} placeholder={coachDefault} data-testid="coach-email-input" />
          {coachEmail && coachDefault && coachEmail !== coachDefault && (
            <p className="text-xs text-[var(--muted-text)] mt-1">Par défaut : {coachDefault}</p>
          )}
        </div>
        <button type="submit" disabled={coachBusy} className="btn-primary disabled:opacity-40" data-testid="coach-email-save"><Check size={14}/> {coachBusy ? "…" : "Enregistrer"}</button>
        <button type="button" onClick={sendDigestNow} className="btn-ghost border" data-testid="coach-digest-send-now"><Bell size={14}/> Envoyer le récap du jour</button>
      </form>

      <div className="flex items-center justify-between flex-wrap gap-2 mt-10 pt-6 border-t">
        <div>
          <h2 className="font-serif-display text-2xl font-semibold" style={{ color: "var(--pine)" }}>Signature email</h2>
          <p className="text-sm text-[var(--muted-text)] mt-1">Ajoutée automatiquement à la fin de chaque email envoyé (publicité, individuel, prospects, relances).</p>
        </div>
      </div>
      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <form onSubmit={save} className="space-y-3">
          <div>
            <label className="text-xs text-[var(--muted-text)] block mb-1">Sous-titre / discipline</label>
            <input className="input-field" value={cfg.tagline || ""} onChange={e => setCfg({ ...cfg, tagline: e.target.value })} placeholder="Nordic Yoga · Marche Nordique" data-testid="signature-tagline" />
          </div>
          <div>
            <label className="text-xs text-[var(--muted-text)] block mb-1">Téléphone</label>
            <input className="input-field" value={cfg.phone || ""} onChange={e => setCfg({ ...cfg, phone: e.target.value })} placeholder="+32 470 12 34 56" data-testid="signature-phone" />
          </div>
          <div>
            <label className="text-xs text-[var(--muted-text)] block mb-1">Email de contact</label>
            <input type="email" className="input-field" value={cfg.contact || ""} onChange={e => setCfg({ ...cfg, contact: e.target.value })} placeholder="coach@nordic-yoga-belgium.be" data-testid="signature-contact" />
          </div>
          <div>
            <label className="text-xs text-[var(--muted-text)] block mb-1">Site web (affiché)</label>
            <input className="input-field" value={cfg.website || ""} onChange={e => setCfg({ ...cfg, website: e.target.value })} placeholder="nordic-yoga-belgium.com" data-testid="signature-website" />
          </div>
          <div>
            <label className="text-xs text-[var(--muted-text)] block mb-1">Phrase de clôture</label>
            <input className="input-field" value={cfg.closing || ""} onChange={e => setCfg({ ...cfg, closing: e.target.value })} placeholder="Merci de nous suivre en pleine nature." data-testid="signature-closing" />
          </div>
          <div>
            <label className="text-xs text-[var(--muted-text)] block mb-1">Logo (image ronde de préférence, max ~1 Mo)</label>
            {cfg.logo_data_url && (
              <div className="flex items-center gap-3 mb-2">
                <img src={cfg.logo_data_url} alt="Logo" width="48" height="48" style={{ borderRadius: "50%", objectFit: "cover" }} />
                <button type="button" onClick={() => setCfg({ ...cfg, logo_data_url: "" })} className="text-xs text-red-600 hover:underline" data-testid="signature-logo-remove">Retirer le logo</button>
              </div>
            )}
            <input type="file" accept="image/*" onChange={e => {
              const f = e.target.files?.[0];
              if (!f) return;
              if (f.size > 1.5 * 1024 * 1024) { toast.error("Image trop volumineuse (max 1.5 Mo)"); e.target.value = ""; return; }
              const reader = new FileReader();
              reader.onload = () => setCfg(c => ({ ...c, logo_data_url: reader.result }));
              reader.readAsDataURL(f);
              e.target.value = "";
            }} className="input-field text-xs" data-testid="signature-logo-upload" />
          </div>
          <button type="submit" disabled={busy} className="btn-primary disabled:opacity-40" data-testid="signature-save"><Check size={14}/> {busy ? "Enregistrement…" : "Enregistrer"}</button>
        </form>
        <div>
          <div className="overline mb-2">Aperçu — rendu dans Gmail / Outlook</div>
          <div className="rounded-xl bg-white border border-slate-200 p-6 max-h-[500px] overflow-auto" data-testid="signature-preview">
            <div style={{ fontFamily: "Arial, sans-serif", color: "#5A6B62", fontSize: 14, lineHeight: 1.5 }}>
              <p>Bonjour <em>Prénom</em>,</p>
              <p>Ceci est un exemple d'email pour visualiser votre signature.</p>
              <p>À très bientôt en pleine nature,</p>
              <div dangerouslySetInnerHTML={{ __html: preview }} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Broadcast() {
  const [form, setForm] = useState({ subject: "", html: "" });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTpl, setSelectedTpl] = useState("");
  const [tplModal, setTplModal] = useState(null); // {mode: "save"|"manage", tpl?}
  const [tplForm, setTplForm] = useState({ name: "", subject: "", html: "" });

  const loadTemplates = () => api.get("/admin/email-templates").then(r => setTemplates(r.data));
  useEffect(() => { loadTemplates(); }, []);

  const applyTemplate = (id) => {
    setSelectedTpl(id);
    const t = templates.find(x => x.id === id);
    if (t) setForm({ subject: t.subject || "", html: t.html || "" });
  };

  const saveAsTemplate = async (e) => {
    e.preventDefault();
    try {
      if (tplModal.tpl) {
        await api.put(`/admin/email-templates/${tplModal.tpl.id}`, tplForm);
        toast.success("Modèle mis à jour");
      } else {
        await api.post("/admin/email-templates", tplForm);
        toast.success("Modèle enregistré");
      }
      setTplModal(null);
      loadTemplates();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteTpl = async (t) => {
    if (!window.confirm(`Supprimer le modèle « ${t.name} » ?`)) return;
    try {
      await api.delete(`/admin/email-templates/${t.id}`);
      toast.success("Modèle supprimé");
      loadTemplates();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addFiles = (fileList) => {
    const arr = Array.from(fileList || []);
    setFiles(prev => [...prev, ...arr]);
  };

  const removeFile = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx));

  const fmtSize = (b) => b < 1024 ? `${b} o` : b < 1024*1024 ? `${(b/1024).toFixed(1)} Ko` : `${(b/1024/1024).toFixed(1)} Mo`;

  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const html = `<div style="font-family:Arial,sans-serif;padding:24px">${form.html.replace(/\n/g, "<br/>")}</div>`;
      const fd = new FormData();
      fd.append("subject", form.subject);
      fd.append("html", html);
      files.forEach(f => fd.append("files", f, f.name));
      const { data } = await api.post("/admin/broadcast", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const parts = [`${data.sent}/${data.queued} email(s) envoyé(s)`];
      if (data.attachments_ignored > 0) parts.push(`${data.attachments_ignored} pièce(s) jointe(s) ignorée(s)`);
      if (data.failed > 0) {
        const firstErr = data.errors?.[0]?.error || "";
        toast.warning(`${parts.join(" — ")} · ${data.failed} échec(s)${firstErr ? " : " + firstErr : ""}`);
      } else {
        toast.success(parts.join(" — "));
      }
      setForm({ subject: "", html: "" });
      setFiles([]);
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || "Erreur lors de l'envoi";
      if (/trop d'envois|rate/i.test(msg)) toast.warning("Envois trop rapprochés — réessayez dans quelques instants");
      else toast.error(msg);
    }
    finally { setBusy(false); }
  };

  const totalSize = files.reduce((a, f) => a + f.size, 0);
  const tooBig = totalSize > 20 * 1024 * 1024;

  return (
    <div>
      <h1 className="font-serif-display text-4xl font-semibold" style={{ color: "var(--pine)" }}>Publicité & communication</h1>
      <p className="mt-2 text-[var(--muted-text)]">Envoyez un email à tous vos participants pour annoncer un nouvel événement ou stage.</p>

      <div className="mt-6 max-w-2xl flex items-center gap-2 flex-wrap" data-testid="broadcast-templates-bar">
        <label className="text-xs text-[var(--muted-text)]">Modèle :</label>
        <select className="input-field !py-2 !w-auto flex-1" value={selectedTpl} onChange={e => e.target.value ? applyTemplate(e.target.value) : setSelectedTpl("")} data-testid="broadcast-template-select">
          <option value="">— aucun —</option>
          {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <button type="button" onClick={() => { setTplForm({ name: "", subject: form.subject, html: form.html }); setTplModal({ mode: "save" }); }} className="btn-ghost border text-sm" data-testid="broadcast-template-save-btn"><Plus size={14}/> Enregistrer comme modèle</button>
        <button type="button" onClick={() => setTplModal({ mode: "manage" })} className="btn-ghost border text-sm" data-testid="broadcast-template-manage-btn">Gérer</button>
      </div>

      <form onSubmit={send} className="mt-4 card-warm p-6 space-y-4 max-w-2xl">
        <input required placeholder="Sujet" className="input-field" value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} data-testid="broadcast-subject" />
        <textarea required rows="10" placeholder="Contenu (texte simple, sauts de ligne acceptés)" className="input-field" value={form.html} onChange={e => setForm({ ...form, html: e.target.value })} data-testid="broadcast-body" />

        <div>
          <label className="block">
            <div className="flex items-center gap-2 cursor-pointer px-4 py-2 border-2 border-dashed border-[var(--forest)]/40 rounded-xl hover:border-[var(--forest)] hover:bg-[var(--forest-light)]/30 transition text-sm" data-testid="broadcast-attach-label">
              <Paperclip size={16} className="text-[var(--forest)]"/>
              <span>Ajouter des pièces jointes (photos, PDF, vidéos…)</span>
            </div>
            <input type="file" multiple className="hidden" onChange={e => { addFiles(e.target.files); e.target.value = ""; }} data-testid="broadcast-attach-input" />
          </label>
          <p className="text-xs text-[var(--muted-text)] mt-1">Total max : 20 Mo</p>
        </div>

        {files.length > 0 && (
          <div className="space-y-2" data-testid="broadcast-attach-list">
            {files.map((f, i) => (
              <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-[var(--sand)] text-sm" data-testid={`broadcast-attach-item-${i}`}>
                <div className="flex items-center gap-2 truncate">
                  <Paperclip size={14} className="text-[var(--forest)] shrink-0"/>
                  <span className="truncate">{f.name}</span>
                  <span className="text-xs text-[var(--muted-text)] shrink-0">({fmtSize(f.size)})</span>
                </div>
                <button type="button" onClick={() => removeFile(i)} className="p-1 hover:bg-red-50 rounded-full text-red-600" data-testid={`broadcast-attach-remove-${i}`}><X size={14}/></button>
              </div>
            ))}
            <div className={`text-xs ${tooBig ? "text-red-600 font-semibold" : "text-[var(--muted-text)]"}`}>
              Total : {fmtSize(totalSize)} {tooBig && "— dépasse 20 Mo, retirez des fichiers"}
            </div>
          </div>
        )}

        <button disabled={busy || tooBig} className="btn-primary disabled:opacity-40" data-testid="broadcast-send"><Send size={16}/>{busy ? "Envoi…" : "Envoyer à tous les participants"}</button>
      </form>

      <SignatureSettings />

      {tplModal && tplModal.mode === "save" && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="template-save-modal">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="p-5 border-b flex items-center justify-between">
              <h2 className="font-serif-display text-2xl" style={{ color: "var(--pine)" }}>{tplModal.tpl ? "Modifier le modèle" : "Nouveau modèle d'email"}</h2>
              <button onClick={() => setTplModal(null)} className="p-1 hover:bg-slate-100 rounded-full"><X size={18}/></button>
            </div>
            <form onSubmit={saveAsTemplate} className="p-5 space-y-4">
              <input required placeholder="Nom du modèle (ex : Bienvenue)" className="input-field" value={tplForm.name} onChange={e => setTplForm({ ...tplForm, name: e.target.value })} data-testid="template-form-name" />
              <input placeholder="Sujet" className="input-field" value={tplForm.subject} onChange={e => setTplForm({ ...tplForm, subject: e.target.value })} data-testid="template-form-subject" />
              <textarea rows="8" placeholder="Contenu (les sauts de ligne sont conservés)" className="input-field" value={tplForm.html} onChange={e => setTplForm({ ...tplForm, html: e.target.value })} data-testid="template-form-html" />
              <div className="flex gap-2 justify-end">
                <button type="button" onClick={() => setTplModal(null)} className="btn-ghost border">Annuler</button>
                <button type="submit" className="btn-primary" data-testid="template-form-submit">Enregistrer</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {tplModal && tplModal.mode === "manage" && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="template-manage-modal">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-auto">
            <div className="p-5 border-b flex items-center justify-between sticky top-0 bg-white">
              <h2 className="font-serif-display text-2xl" style={{ color: "var(--pine)" }}>Modèles d'email</h2>
              <button onClick={() => setTplModal(null)} className="p-1 hover:bg-slate-100 rounded-full"><X size={18}/></button>
            </div>
            <div className="p-5 space-y-2">
              {templates.length === 0 && <div className="text-sm text-[var(--muted-text)] text-center py-8">Aucun modèle enregistré. Créez-en depuis le bouton « Enregistrer comme modèle ».</div>}
              {templates.map(t => (
                <div key={t.id} className="p-3 rounded-lg bg-[var(--sand)] flex items-center justify-between gap-3" data-testid={`template-row-${t.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-[var(--pine)] truncate">{t.name}</div>
                    <div className="text-xs text-[var(--muted-text)] truncate">{t.subject || "(sans sujet)"}</div>
                  </div>
                  <button onClick={() => { setTplForm({ name: t.name, subject: t.subject, html: t.html }); setTplModal({ mode: "save", tpl: t }); }} className="text-[var(--pine)] text-xs p-2 hover:bg-slate-100 rounded-full" data-testid={`template-edit-${t.id}`}><Pencil size={14}/></button>
                  <button onClick={() => deleteTpl(t)} className="text-red-600 text-xs p-2 hover:bg-red-50 rounded-full" data-testid={`template-delete-${t.id}`}><Trash2 size={14}/></button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Admins() {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", phone: "", role: "coach", send_credentials: true });
  const load = () => api.get("/admin/admins").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/admins", form);
      toast.success(data.email_sent ? "Compte créé — email envoyé" : "Compte créé");
      setCreating(false);
      setForm({ email: "", password: "", name: "", phone: "", role: "coach", send_credentials: true });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce compte ?")) return;
    try {
      await api.delete(`/admin/admins/${id}`);
      toast.success("Compte supprimé");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const resetPwd = async (id, email) => {
    const custom = window.prompt(`Réinitialiser le mot de passe de ${email}.\n\nLaissez vide pour générer un mot de passe aléatoire, ou saisissez un nouveau mot de passe (min 6 caractères) :`, "");
    if (custom === null) return;
    try {
      const { data } = await api.post(`/admin/users/${id}/reset-password`, {
        new_password: custom.trim() || null,
        send_email: true,
        force_logout: true,
      });
      if (data.generated_password) {
        window.prompt("Nouveau mot de passe généré — copiez-le maintenant :", data.generated_password);
      }
      toast.success(data.email_sent ? "Mot de passe réinitialisé — email envoyé" : "Mot de passe réinitialisé");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-serif-display text-4xl font-semibold" style={{ color: "var(--pine)" }}>Équipe</h1>
        <button onClick={() => setCreating(!creating)} className="btn-primary" data-testid="admin-new-admin">
          <Plus size={16}/> Ajouter un membre
        </button>
      </div>
      <p className="mt-2 text-[var(--muted-text)]">
        <strong>Admin</strong> : accès complet (séances, participants, paiements, facturation, publicité).<br/>
        <strong>Coach</strong> : accès limité (séances + pointage / check-in QR uniquement).
      </p>

      {creating && (
        <form onSubmit={save} className="card-warm p-6 mt-6 grid sm:grid-cols-2 gap-4">
          <input required placeholder="Nom complet" className="input-field" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="admin-form-name" />
          <input placeholder="Téléphone (optionnel)" className="input-field" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} data-testid="admin-form-phone" />
          <input required type="email" placeholder="Email" className="input-field" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="admin-form-email" />
          <input required type="password" placeholder="Mot de passe (min 6 caractères)" className="input-field" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} data-testid="admin-form-password" />
          <select className="input-field" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} data-testid="admin-form-role">
            <option value="coach">Coach (pointage seulement)</option>
            <option value="admin">Admin (accès complet)</option>
          </select>
          <label className="flex items-center gap-2 text-sm text-[var(--muted-text)]">
            <input type="checkbox" checked={form.send_credentials} onChange={e => setForm({ ...form, send_credentials: e.target.checked })} data-testid="admin-form-send-credentials" />
            Envoyer les identifiants par email
          </label>
          <button type="submit" className="btn-primary sm:col-span-2 justify-center" data-testid="admin-form-submit">Créer le compte</button>
        </form>
      )}

      <div className="mt-6 card-warm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[var(--sand)] text-left"><tr><th className="p-4">Nom</th><th className="p-4">Email</th><th className="p-4">Rôle</th><th className="p-4">Créé le</th><th className="p-4 text-right">Actions</th></tr></thead>
          <tbody>
            {items.map(a => (
              <tr key={a.id} className="border-t" data-testid={`admin-row-${a.id}`}>
                <td className="p-4 font-medium flex items-center gap-2"><ShieldCheck size={14} className="text-[var(--forest)]"/> {a.name}{me?.id === a.id && <span className="text-xs text-[var(--muted-text)]">(vous)</span>}</td>
                <td className="p-4">{a.email}</td>
                <td className="p-4"><span className={`badge-discipline ${a.role === "admin" ? "badge-stage" : "badge-marche"}`}>{a.role === "admin" ? "Admin" : "Coach"}</span></td>
                <td className="p-4">{a.created_at && new Date(a.created_at).toLocaleDateString("fr-FR")}</td>
                <td className="p-4 text-right">
                  <div className="inline-flex gap-1 justify-end">
                    <button onClick={() => resetPwd(a.id, a.email)} title="Réinitialiser mot de passe" className="p-2 rounded-full text-[var(--terracotta)] hover:bg-orange-50" data-testid={`admin-reset-pwd-${a.id}`}><Repeat2 size={14}/></button>
                    {me?.id !== a.id && (
                      <button onClick={() => remove(a.id)} className="text-red-600 hover:bg-red-50 p-2 rounded-full" data-testid={`admin-delete-${a.id}`}><Trash2 size={14}/></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const ACTION_LABELS = {
  session_create: "Création de séance",
  session_update: "Modification de séance",
  session_delete: "Suppression de séance",
  attendance_toggle: "Pointage présence",
  checkin: "Check-in QR",
  payment_mark_paid: "Marquage payé",
  carnet_grant: "Attribution carnet",
  staff_create: "Création membre équipe",
  staff_delete: "Suppression membre équipe",
  broadcast_send: "Envoi publicité",
};

function Activity() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ actions: [], actor_emails: [] });
  const [filters, setFilters] = useState({ action: "", actor_email: "", from_date: "", to_date: "" });

  const buildQuery = () => {
    const p = new URLSearchParams();
    if (filters.action) p.set("action", filters.action);
    if (filters.actor_email) p.set("actor_email", filters.actor_email);
    if (filters.from_date) p.set("from_date", new Date(filters.from_date).toISOString());
    if (filters.to_date) {
      const d = new Date(filters.to_date);
      d.setHours(23, 59, 59, 999);
      p.set("to_date", d.toISOString());
    }
    return p.toString();
  };

  const load = () => {
    const q = buildQuery();
    api.get(`/admin/activity${q ? "?" + q : ""}`).then(r => setItems(r.data));
  };

  useEffect(() => {
    api.get("/admin/activity/actions").then(r => setMeta(r.data));
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filters]);

  const reset = () => setFilters({ action: "", actor_email: "", from_date: "", to_date: "" });

  const exportCsv = async () => {
    const q = buildQuery();
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/activity/export.csv${q ? "?" + q : ""}`;
    try {
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error(res.statusText);
      const blob = await res.blob();
      const disposition = res.headers.get("content-disposition") || "";
      const match = disposition.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : "journal-activite.csv";
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(a.href);
      toast.success("Export CSV téléchargé");
    } catch (e) {
      toast.error("Erreur lors de l'export");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-serif-display text-4xl font-semibold" style={{ color: "var(--pine)" }}>Journal d'activité</h1>
        <button onClick={exportCsv} className="btn-primary" data-testid="activity-export-csv">
          <Download size={16}/> Exporter en CSV
        </button>
      </div>
      <p className="mt-2 text-[var(--muted-text)]">Historique complet des actions administrateurs et coachs. Filtrez et exportez pour archivage RGPD.</p>

      <div className="card-warm p-5 mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="activity-filters">
        <div>
          <label className="text-xs text-[var(--muted-text)] block mb-1"><Filter size={12} className="inline mr-1"/>Action</label>
          <select className="input-field" value={filters.action} onChange={e => setFilters({ ...filters, action: e.target.value })} data-testid="activity-filter-action">
            <option value="">Toutes</option>
            {meta.actions.map(a => <option key={a} value={a}>{ACTION_LABELS[a] || a}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-[var(--muted-text)] block mb-1">Auteur</label>
          <select className="input-field" value={filters.actor_email} onChange={e => setFilters({ ...filters, actor_email: e.target.value })} data-testid="activity-filter-actor">
            <option value="">Tous</option>
            {meta.actor_emails.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-[var(--muted-text)] block mb-1">Du</label>
          <input type="date" className="input-field" value={filters.from_date} onChange={e => setFilters({ ...filters, from_date: e.target.value })} data-testid="activity-filter-from" />
        </div>
        <div>
          <label className="text-xs text-[var(--muted-text)] block mb-1">Au</label>
          <input type="date" className="input-field" value={filters.to_date} onChange={e => setFilters({ ...filters, to_date: e.target.value })} data-testid="activity-filter-to" />
        </div>
        <div className="sm:col-span-2 lg:col-span-4 flex items-center justify-between text-xs text-[var(--muted-text)]">
          <span>{items.length} événement{items.length > 1 ? "s" : ""} affiché{items.length > 1 ? "s" : ""}</span>
          <button onClick={reset} className="hover:text-[var(--pine)]" data-testid="activity-filter-reset">Réinitialiser les filtres</button>
        </div>
      </div>

      <div className="mt-6 card-warm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[var(--sand)] text-left">
            <tr><th className="p-4">Date</th><th className="p-4">Auteur</th><th className="p-4">Action</th><th className="p-4">Détail</th></tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan="4" className="p-8 text-center text-[var(--muted-text)]">Aucune activité enregistrée pour ces filtres.</td></tr>}
            {items.map(a => {
              const details = Object.entries(a.meta || {}).map(([k, v]) => `${k}: ${v}`).join(" · ");
              return (
                <tr key={a.id} className="border-t" data-testid={`activity-row-${a.id}`}>
                  <td className="p-4 whitespace-nowrap text-[var(--muted-text)]">{new Date(a.at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}</td>
                  <td className="p-4"><div className="font-medium">{a.actor_name || "—"}</div><div className="text-xs text-[var(--muted-text)]">{a.actor_email} · {a.actor_role}</div></td>
                  <td className="p-4">{ACTION_LABELS[a.action] || a.action}</td>
                  <td className="p-4 text-[var(--muted-text)]">{details}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CoachRestricted() {
  return (
    <div className="max-w-lg mx-auto text-center py-20" data-testid="coach-restricted">
      <ShieldCheck size={40} className="mx-auto text-[var(--forest)]" />
      <h2 className="font-serif-display text-3xl font-semibold mt-4" style={{ color: "var(--pine)" }}>Accès admin requis</h2>
      <p className="mt-3 text-[var(--muted-text)]">Cette section est réservée aux administrateurs. En tant que coach, vous pouvez consulter l'agenda et pointer les présences.</p>
    </div>
  );
}

function Reminders() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => { api.get("/admin/settings/reminders").then(r => setSettings(r.data)); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings/reminders", {
        enabled: settings.enabled,
        hours_before: Number(settings.hours_before) || 48,
        subject_template: settings.subject_template,
        body_template: settings.body_template,
      });
      toast.success("Rappels enregistrés");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/admin/reminders/send-now");
      if (data.skipped) toast.info("Les rappels sont actuellement désactivés.");
      else toast.success(`${data.sent} rappel(s) envoyé(s) · ${data.sessions_checked} séance(s) contrôlée(s)`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  if (!settings) return <div>Chargement…</div>;

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-serif-display text-4xl font-semibold" style={{ color: "var(--pine)" }}>Rappels automatiques</h1>
        <button onClick={sendNow} disabled={sending} className="btn-ghost border" data-testid="reminder-send-now">
          <Bell size={16}/> {sending ? "Envoi…" : "Envoyer les rappels dus maintenant"}
        </button>
      </div>
      <p className="mt-2 text-[var(--muted-text)]">
        Envoie automatiquement un email aux participants inscrits <strong>{settings.hours_before}h avant</strong> le début de leur séance. Vérifié chaque heure par le planificateur.
      </p>

      <div className="mt-6 card-warm p-6 space-y-5 max-w-2xl" data-testid="reminder-settings">
        <label className="flex items-center gap-3 cursor-pointer">
          <input type="checkbox" className="w-5 h-5" checked={settings.enabled} onChange={e => setSettings({ ...settings, enabled: e.target.checked })} data-testid="reminder-enabled" />
          <div>
            <div className="font-medium" style={{ color: "var(--pine)" }}>Activer les rappels automatiques</div>
            <div className="text-xs text-[var(--muted-text)]">Décochez pour mettre en pause sans perdre le modèle</div>
          </div>
        </label>

        <div>
          <label className="text-xs text-[var(--muted-text)] block mb-1"><Clock size={12} className="inline mr-1"/>Nombre d'heures avant la séance</label>
          <div className="flex items-center gap-3">
            <input type="number" min="1" max="168" className="input-field w-32" value={settings.hours_before} onChange={e => setSettings({ ...settings, hours_before: e.target.value })} data-testid="reminder-hours" />
            <span className="text-sm text-[var(--muted-text)]">heures (ex : 24, 48, 72)</span>
          </div>
        </div>

        <div>
          <label className="text-xs text-[var(--muted-text)] block mb-1">Sujet de l'email</label>
          <input className="input-field" value={settings.subject_template} onChange={e => setSettings({ ...settings, subject_template: e.target.value })} data-testid="reminder-subject" />
        </div>

        <div>
          <label className="text-xs text-[var(--muted-text)] block mb-1">Corps de l'email</label>
          <textarea rows="10" className="input-field font-mono text-sm" value={settings.body_template} onChange={e => setSettings({ ...settings, body_template: e.target.value })} data-testid="reminder-body" />
          <div className="text-xs text-[var(--muted-text)] mt-2">
            Variables disponibles :&nbsp;
            <code className="bg-slate-100 px-1 rounded">{"{name}"}</code>&nbsp;
            <code className="bg-slate-100 px-1 rounded">{"{session_title}"}</code>&nbsp;
            <code className="bg-slate-100 px-1 rounded">{"{when}"}</code>&nbsp;
            <code className="bg-slate-100 px-1 rounded">{"{location}"}</code>&nbsp;
            <code className="bg-slate-100 px-1 rounded">{"{hours}"}</code>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t">
          <button onClick={save} disabled={saving} className="btn-primary" data-testid="reminder-save">
            {saving ? "…" : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
}

function EmailTracking() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ kinds: [], recipients: [] });
  const [filters, setFilters] = useState({ kind: "", to_email: "" });

  const load = () => {
    const p = new URLSearchParams();
    if (filters.kind) p.set("kind", filters.kind);
    if (filters.to_email) p.set("to_email", filters.to_email);
    api.get(`/admin/emails${p.toString() ? "?" + p : ""}`).then(r => setItems(r.data));
  };

  useEffect(() => { api.get("/admin/emails/kinds").then(r => setMeta(r.data)); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filters]);

  const KIND_LABEL = {
    broadcast: "Publicité", individual: "Individuel", reminder: "Rappel",
    reset: "Reset mdp", generic: "Autre",
  };

  const openRate = items.length ? Math.round(items.filter(i => i.opened_at).length / items.length * 100) : 0;

  return (
    <div>
      <h1 className="font-serif-display text-4xl font-semibold" style={{ color: "var(--pine)" }}>Suivi des emails</h1>
      <p className="mt-2 text-[var(--muted-text)]">Chaque email envoyé est traçé. L'ouverture est détectée via un pixel invisible (les clients qui bloquent les images ne seront pas comptabilisés).</p>

      <div className="mt-6 grid sm:grid-cols-3 gap-4">
        <div className="card-warm p-5" data-testid="email-stat-total"><div className="overline">Envoyés</div><div className="font-serif-display text-4xl mt-2" style={{ color: "var(--pine)" }}>{items.length}</div></div>
        <div className="card-warm p-5" data-testid="email-stat-opened"><div className="overline">Ouverts</div><div className="font-serif-display text-4xl mt-2" style={{ color: "var(--pine)" }}>{items.filter(i => i.opened_at).length}</div></div>
        <div className="card-warm p-5" data-testid="email-stat-rate"><div className="overline">Taux d'ouverture</div><div className="font-serif-display text-4xl mt-2" style={{ color: "var(--terracotta)" }}>{openRate}%</div></div>
      </div>

      <div className="card-warm p-4 mt-6 grid sm:grid-cols-3 gap-3" data-testid="email-filters">
        <div>
          <label className="text-xs text-[var(--muted-text)] block mb-1">Type</label>
          <select className="input-field" value={filters.kind} onChange={e => setFilters({ ...filters, kind: e.target.value })} data-testid="email-filter-kind">
            <option value="">Tous</option>
            {meta.kinds.map(k => <option key={k} value={k}>{KIND_LABEL[k] || k}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs text-[var(--muted-text)] block mb-1">Destinataire</label>
          <select className="input-field" value={filters.to_email} onChange={e => setFilters({ ...filters, to_email: e.target.value })} data-testid="email-filter-recipient">
            <option value="">Tous</option>
            {meta.recipients.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
      </div>

      <div className="mt-4 card-warm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[var(--sand)] text-left">
            <tr><th className="p-4">Envoyé le</th><th className="p-4">Destinataire</th><th className="p-4">Sujet</th><th className="p-4">Type</th><th className="p-4">Ouvert</th><th className="p-4">Dernière ouverture</th></tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan="6" className="p-8 text-center text-[var(--muted-text)]">Aucun email envoyé pour ce filtre.</td></tr>}
            {items.map(e => (
              <tr key={e.id} className="border-t" data-testid={`email-row-${e.id}`}>
                <td className="p-4 whitespace-nowrap text-[var(--muted-text)]">{e.sent_at ? new Date(e.sent_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—"}</td>
                <td className="p-4">{e.to_email}</td>
                <td className="p-4 truncate max-w-[300px]" title={e.subject}>{e.subject}</td>
                <td className="p-4"><span className="badge-discipline badge-stage">{KIND_LABEL[e.kind] || e.kind}</span></td>
                <td className="p-4">
                  {e.opened_at ? <span className="badge-discipline badge-yoga">Oui ({e.open_count}×)</span> : <span className="badge-discipline badge-marche">Non</span>}
                </td>
                <td className="p-4 text-[var(--muted-text)] whitespace-nowrap">{e.last_opened_at ? new Date(e.last_opened_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- Carnets / Abonnements management ----------
function CarnetTypesAdmin() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = { name: "", sessions_count: 5, price_eur: 0, description: "", unlimited: false, validity_days: 0, active: true };
  const [form, setForm] = useState(empty);
  const load = () => api.get("/admin/carnets/types").then(r => setItems(r.data)).catch(e => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);
  const openNew = () => { setForm(empty); setEditing(null); setShowForm(true); };
  const openEdit = (c) => {
    setForm({
      name: c.name || "", sessions_count: c.sessions_count || 1,
      price_eur: c.price_eur || 0, description: c.description || "",
      unlimited: !!c.unlimited, validity_days: c.validity_days || 0,
      active: c.active !== false,
    });
    setEditing(c); setShowForm(true);
  };
  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      name: form.name.trim(),
      sessions_count: parseInt(form.sessions_count) || 1,
      price_eur: parseFloat(form.price_eur) || 0,
      description: form.description || "",
      unlimited: !!form.unlimited,
      validity_days: parseInt(form.validity_days) || 0,
      active: !!form.active,
    };
    try {
      if (editing) await api.put(`/admin/carnets/types/${editing.id}`, payload);
      else await api.post("/carnets", payload);
      toast.success(editing ? "Abonnement mis à jour" : "Abonnement créé");
      setShowForm(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Supprimer « ${c.name} » ? Si des abonnements existent, le carnet sera désactivé.`)) return;
    try {
      const { data } = await api.delete(`/admin/carnets/types/${c.id}`);
      toast.success(data.deactivated ? `Désactivé (${data.subscriptions} abonnement(s) actif(s))` : "Supprimé");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="font-serif-display text-4xl font-semibold" style={{ color: "var(--pine)" }}>Abonnements & Carnets</h1>
          <p className="text-[var(--muted-text)] mt-1">Modifiez les prix, créez de nouvelles formules ou désactivez celles obsolètes.</p>
        </div>
        <button onClick={openNew} className="btn-primary text-sm" data-testid="admin-carnet-new"><Plus size={14}/> Nouvelle formule</button>
      </div>
      {showForm && (
        <form onSubmit={submit} className="card-warm p-6 mb-6 grid md:grid-cols-2 gap-3" data-testid="admin-carnet-form">
          <input required className="input-field md:col-span-2" placeholder="Nom (ex : Carnet 5 séances) *" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="carnet-name" />
          <div>
            <label className="text-xs text-[var(--muted-text)] uppercase">Nombre de séances *</label>
            <input required type="number" min="1" className="input-field mt-1" value={form.sessions_count} onChange={e => setForm({ ...form, sessions_count: e.target.value })} data-testid="carnet-sessions" />
          </div>
          <div>
            <label className="text-xs text-[var(--muted-text)] uppercase">Prix TTC (€) *</label>
            <input required type="number" step="0.01" min="0" className="input-field mt-1" value={form.price_eur} onChange={e => setForm({ ...form, price_eur: e.target.value })} data-testid="carnet-price" />
          </div>
          <div>
            <label className="text-xs text-[var(--muted-text)] uppercase">Durée de validité (jours)</label>
            <input type="number" min="0" className="input-field mt-1" value={form.validity_days} onChange={e => setForm({ ...form, validity_days: e.target.value })} placeholder="0 = sans expiration" data-testid="carnet-validity" />
            <p className="text-[10px] text-[var(--muted-text)] mt-1">0 = pas d'expiration · 30 = 1 mois · 90 = trimestre · 365 = 1 an</p>
          </div>
          <div className="flex flex-col gap-2 justify-center">
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.unlimited} onChange={e => setForm({ ...form, unlimited: e.target.checked })} data-testid="carnet-unlimited" />
              Accès illimité pendant la période
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} data-testid="carnet-active" />
              Formule active (visible aux participants)
            </label>
          </div>
          <textarea className="input-field md:col-span-2" rows={2} placeholder="Description (optionnel)" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} data-testid="carnet-desc" />
          <div className="md:col-span-2 flex gap-2 justify-end">
            <button type="button" onClick={() => setShowForm(false)} className="btn-ghost border text-sm">Annuler</button>
            <button type="submit" className="btn-primary text-sm" data-testid="carnet-save"><Check size={14}/> Enregistrer</button>
          </div>
        </form>
      )}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(c => (
          <div key={c.id} className="card-warm p-5 flex flex-col" data-testid={`admin-carnet-${c.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-serif-display text-xl truncate" style={{ color: "var(--pine)" }}>{c.name}</div>
                <div className="text-xs text-[var(--muted-text)] mt-1">
                  {c.unlimited ? "Accès illimité" : `${c.sessions_count} séance(s)`}
                  {c.validity_days > 0 ? ` · validité ${c.validity_days} j` : ""}
                </div>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-[10px] ${c.active === false ? "bg-slate-200 text-slate-600" : "bg-emerald-100 text-emerald-800"}`}>
                {c.active === false ? "Inactif" : "Actif"}
              </span>
            </div>
            {c.description && <p className="text-sm text-[var(--muted-text)] mt-3 line-clamp-2">{c.description}</p>}
            <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
              <span className="font-semibold text-2xl" style={{ color: "var(--terracotta)" }}>{Number(c.price_eur || 0).toFixed(2)} €</span>
              <div className="flex gap-2">
                <button onClick={() => openEdit(c)} className="btn-ghost border text-xs" data-testid={`admin-carnet-edit-${c.id}`}><Pencil size={12}/> Modifier</button>
                <button onClick={() => remove(c)} className="text-red-600 hover:text-red-700" data-testid={`admin-carnet-delete-${c.id}`}><Trash2 size={14}/></button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="col-span-full text-center text-[var(--muted-text)] py-12">Aucune formule — cliquez « Nouvelle formule » pour commencer.</div>}
      </div>
    </div>
  );
}



export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const doLogout = async () => { await logout(); nav("/"); };
  const isAdmin = user?.role === "admin";
  const allLinks = [
    { to: "/admin", label: "Tableau de bord", icon: BarChart3, end: true, adminOnly: false, key: "overview" },
    { to: "/admin/sessions", label: "Séances", icon: Calendar, adminOnly: false, key: "sessions" },
    { to: "/admin/participants", label: "Participants", icon: Users, adminOnly: true, key: "participants" },
    { to: "/admin/prospects", label: "Prospects", icon: UserPlus, adminOnly: true, key: "prospects" },
    { to: "/admin/paiements", label: "Paiements", icon: CreditCard, adminOnly: true, key: "paiements" },
    { to: "/admin/abonnements", label: "Abonnements", icon: Euro, adminOnly: true, key: "abonnements" },
    { to: "/admin/publicite", label: "Publicité", icon: Send, adminOnly: true, key: "publicite" },
    { to: "/admin/boutique", label: "Boutique", icon: ShoppingBag, adminOnly: true, key: "boutique" },
    { to: "/admin/rappels", label: "Rappels auto", icon: Bell, adminOnly: true, key: "rappels" },
    { to: "/admin/emails", label: "Suivi emails", icon: Mail, adminOnly: true, key: "emails" },
    { to: "/admin/administrateurs", label: "Équipe", icon: ShieldCheck, adminOnly: true, key: "administrateurs" },
    { to: "/admin/journal", label: "Journal", icon: History, adminOnly: true, key: "journal" },
  ];
  const links = allLinks.filter(l => isAdmin || !l.adminOnly);
  const sidebarContent = (
    <>
      <div className="flex items-center justify-between">
        <div>
          <div className="font-serif-display text-xl font-semibold text-white">Nordic Yoga Belgium</div>
          <div className="text-xs text-white/60 mt-1">{isAdmin ? "Admin" : "Coach"}</div>
        </div>
        <button
          onClick={() => setMenuOpen(false)}
          className="md:hidden text-white/70 hover:text-white p-1"
          aria-label="Fermer le menu"
          data-testid="admin-menu-close">
          <X size={22}/>
        </button>
      </div>
      <nav className="mt-8 space-y-1 flex-1 overflow-y-auto">
        {links.map(l => (
          <NavLink key={l.key} to={l.to} end={l.end}
            onClick={() => setMenuOpen(false)}
            className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
            data-testid={`admin-nav-${l.key}`}>
            <l.icon size={16}/> {l.label}
          </NavLink>
        ))}
      </nav>
      <div className="mt-8 pt-6 border-t border-white/10">
        <div className="text-xs text-white/60 mb-2 break-all">{user?.email}</div>
        <button onClick={doLogout} className="nav-link w-full" data-testid="admin-logout"><LogOut size={16}/> Déconnexion</button>
      </div>
    </>
  );
  return (
    <div className="min-h-screen flex" style={{ background: "var(--sand)" }}>
      {/* Desktop sidebar */}
      <aside className="dashboard-nav w-64 p-6 hidden md:flex flex-col">
        {sidebarContent}
      </aside>
      {/* Mobile overlay + drawer */}
      {menuOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={() => setMenuOpen(false)}
          data-testid="admin-menu-overlay"
        />
      )}
      <aside
        className={`dashboard-nav fixed inset-y-0 left-0 z-50 w-72 p-6 flex flex-col md:hidden transform transition-transform duration-200 ${menuOpen ? "translate-x-0" : "-translate-x-full"}`}
        data-testid="admin-mobile-drawer">
        {sidebarContent}
      </aside>
      <main className="flex-1 overflow-auto min-w-0">
        <div className="sticky top-0 z-10 flex items-center justify-between px-4 sm:px-8 md:px-12 py-4 border-b bg-[var(--sand)]/90 backdrop-blur">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setMenuOpen(true)}
              className="md:hidden btn-ghost text-sm inline-flex items-center gap-1.5 border"
              aria-label="Ouvrir le menu"
              data-testid="admin-menu-open">
              <Menu size={18}/>
            </button>
            <button onClick={() => nav(-1)} className="btn-ghost text-sm inline-flex items-center gap-1.5 border" data-testid="admin-back-btn">
              <ArrowLeft size={16}/> <span className="hidden sm:inline">Précédent</span>
            </button>
            <button onClick={() => nav("/admin")} className="btn-ghost text-sm inline-flex items-center gap-1.5 border" data-testid="admin-home-btn">
              <Home size={16}/> <span className="hidden sm:inline">Accueil</span>
            </button>
          </div>
          <a href="/" className="text-sm text-[var(--muted-text)] hover:text-[var(--pine)] inline-flex items-center gap-1.5" data-testid="admin-view-site">
            <span className="hidden sm:inline">Voir le site public</span><ExternalLink size={14}/>
          </a>
        </div>
        <div className="p-4 sm:p-8 md:p-12">
          <Routes>
            <Route index element={<Overview />} />
            <Route path="sessions" element={<Sessions />} />
            {isAdmin ? <Route path="participants" element={<Participants />} /> : <Route path="participants" element={<CoachRestricted />} />}
            {isAdmin ? <Route path="prospects" element={<Prospects />} /> : <Route path="prospects" element={<CoachRestricted />} />}
            {isAdmin ? <Route path="paiements" element={<Payments />} /> : <Route path="paiements" element={<CoachRestricted />} />}
            {isAdmin ? <Route path="abonnements" element={<CarnetTypesAdmin />} /> : <Route path="abonnements" element={<CoachRestricted />} />}
            {isAdmin ? <Route path="publicite" element={<Broadcast />} /> : <Route path="publicite" element={<CoachRestricted />} />}
            {isAdmin ? <Route path="boutique" element={<AdminShop />} /> : <Route path="boutique" element={<CoachRestricted />} />}
            {isAdmin ? <Route path="rappels" element={<Reminders />} /> : <Route path="rappels" element={<CoachRestricted />} />}
            {isAdmin ? <Route path="emails" element={<EmailTracking />} /> : <Route path="emails" element={<CoachRestricted />} />}
            {isAdmin ? <Route path="administrateurs" element={<Admins />} /> : <Route path="administrateurs" element={<CoachRestricted />} />}
            {isAdmin ? <Route path="journal" element={<Activity />} /> : <Route path="journal" element={<CoachRestricted />} />}
          </Routes>
        </div>
      </main>
    </div>
  );
}
